import { useEffect, useMemo, useState, useRef, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { format } from "date-fns";
import { cn } from "../lib/utils";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Checkbox } from "../components/ui/checkbox";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { ClickableAvatar } from "../components/ClickableAvatar";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";
import { Switch } from "../components/ui/switch";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../components/ui/popover";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogDescription,
  DialogFooter,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../components/ui/tooltip";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { toast } from "sonner";
import {
  ArrowDown,
  ArrowLeft,
  ArrowUp,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  ChevronUp,
  Circle,
  ClipboardCopy,
  ClipboardPaste,
  CloudUpload,
  Copy,
  Plus,
  Save,
  Settings,
  Trash2,
  X,
  Building2,
  Dumbbell,
  Search,
  Loader2,
  FolderOpen,
  Pencil,
} from "lucide-react";
import { MultiSelect } from "../components/ui/multi-select";
import DetailedWorkoutFields, {
  makeDefaultWorkoutFields,
  normalizeWorkoutFields,
} from "../components/workouts/DetailedWorkoutFields";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const DAYS = [
  { key: "sunday", label: "Sun" },
  { key: "monday", label: "Mon" },
  { key: "tuesday", label: "Tue" },
  { key: "wednesday", label: "Wed" },
  { key: "thursday", label: "Thu" },
  { key: "friday", label: "Fri" },
  { key: "saturday", label: "Sat" },
];

const DEFAULT_SECTIONS = ["Workout"];

const MISSED_POLICIES = [
  { value: "skip_missed_workout", label: "Skip Missed Workout" },
  { value: "move_to_next_day", label: "Move To Next Day" },
];

function makeWorkout() {
  return {
    clientId: crypto.randomUUID(),
    workoutId: "",
    title: "",
    groupName: "",
    ...makeDefaultWorkoutFields(),
  };
}

function makeSection(name) {
  return {
    id: crypto.randomUUID(),
    name,
    workoutCount: 1,
    workouts: [makeWorkout()],
    notes: "",
  };
}

function makeDayPlan(phase) {
  return {
    label: "",
    notes: "",
    sections: phase === "active" ? DEFAULT_SECTIONS.map(makeSection) : [],
  };
}

function makePlans(phase, count) {
  return Object.fromEntries(
    Array.from({ length: Math.max(1, count) }, (_, index) => [
      `day-${index + 1}`,
      makeDayPlan(phase),
    ]),
  );
}

function isDayComplete(day) {
  if (!day?.sections || day.sections.length === 0) return false;
  const allWorkouts = day.sections.flatMap((section) => section.workouts || []);
  if (allWorkouts.length === 0) return false;
  return allWorkouts.every(
    (workout) => (workout.title || "").trim() || workout.workoutId,
  );
}

export default function MemberWorkoutCardBuilder() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(window.location.search);
  const reassignId = queryParams.get("reassignId");
  const templateId = queryParams.get("templateId");
  const [member, setMember] = useState(null);
  const [trainers, setTrainers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [workouts, setWorkouts] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [templateSearch, setTemplateSearch] = useState("");
  const [templateList, setTemplateList] = useState([]);
  const [templateSearchLoading, setTemplateSearchLoading] = useState(false);
  const [isTemplatePopoverOpen, setIsTemplatePopoverOpen] = useState(false);
  const [selectedTemplateToLoad, setSelectedTemplateToLoad] = useState(null);
  const [showTemplateConfirmDialog, setShowTemplateConfirmDialog] =
    useState(false);

  const [form, setForm] = useState({
    cardName: "",
    numberOfWeeks: 6,
    fitnessPreparationDays: 3,
    fitnessRePreparationDays: 3,
    startDate: format(new Date(), "yyyy-MM-dd"),
    restDays: ["sunday"],
    missedWorkoutPolicy: "move_to_next_day",
    trainerId: "none",
    notes: "",
    saveAsTemplate: false,
    templateName: "",
    branchIds: [],
    allowMemberToComplete: true,
    enableTimer: false,
    isFlexible: true,
  });

  const activeDayCount = Math.max(1, 7 - form.restDays.length);
  const restDayCount = Math.max(1, form.restDays.length);

  const [activeTab, setActiveTab] = useState({
    preparation: "all-day",
    repreparation: "all-day",
    active: "all-day",
    rest: "all-day",
  });
  const [plans, setPlans] = useState({
    preparation: makePlans("preparation", 3),
    repreparation: makePlans("repreparation", 3),
    active: makePlans("active", 6),
    rest: makePlans("rest", 1),
    globalCommon: { common: makeDayPlan("globalCommon") },
    preparationCommon: { common: makeDayPlan("preparationCommon") },
    repreparationCommon: { common: makeDayPlan("repreparationCommon") },
    activeCommon: { common: makeDayPlan("activeCommon") },
    restCommon: { common: makeDayPlan("restCommon") },
    preparationCommonPost: { common: makeDayPlan("preparationCommonPost") },
    repreparationCommonPost: { common: makeDayPlan("repreparationCommonPost") },
    activeCommonPost: { common: makeDayPlan("activeCommonPost") },
    restCommonPost: { common: makeDayPlan("restCommonPost") },
  });
  const [showGlobalCommon, setShowGlobalCommon] = useState(false);
  const [showPhaseCommon, setShowPhaseCommon] = useState({
    preparation: true,
    repreparation: true,
    active: true,
    rest: true,
  });
  const [showPhaseCommonPost, setShowPhaseCommonPost] = useState({
    preparation: true,
    repreparation: true,
    active: true,
    rest: true,
  });
  const [enabledTabs, setEnabledTabs] = useState({
    preparation: false,
    active: true,
    rest: false,
    repreparation: false,
  });
  const [activePhaseTab, setActivePhaseTab] = useState("active");

  const handleTabToggle = (phase, checked) => {
    const nextEnabled = { ...enabledTabs, [phase]: checked };
    const enabledKeys = Object.keys(nextEnabled).filter((k) => nextEnabled[k]);
    if (enabledKeys.length === 0) {
      toast.error("At least one phase must be enabled");
      return;
    }
    setEnabledTabs(nextEnabled);
    if (!checked && activePhaseTab === phase) {
      setActivePhaseTab(enabledKeys[0]);
      setActiveWorkoutKey("");
    }
  };

  const [enableDaySpecific, setEnableDaySpecific] = useState({
    preparation: false,
    repreparation: false,
    rest: false,
  });
  const [activeStep, setActiveStep] = useState("configuration");
  const [activeWorkoutKey, setActiveWorkoutKey] = useState("");
  const [collapsedSections, setCollapsedSections] = useState({});
  const [deleteSectionConfirm, setDeleteSectionConfirm] = useState(null);
  const [deleteWorkoutConfirm, setDeleteWorkoutConfirm] = useState(null);
  const [copyPlanConfirm, setCopyPlanConfirm] = useState(null);
  const [restDayConfirm, setRestDayConfirm] = useState(null);
  const [addSectionDialog, setAddSectionDialog] = useState(null);
  const [newSectionName, setNewSectionName] = useState("");
  const [cardNameError, setCardNameError] = useState(false);
  const [copiedWorkout, setCopiedWorkout] = useState(null);
  const [copiedSection, setCopiedSection] = useState(null);
  const [draftAssignment, setDraftAssignment] = useState(null);
  const [showResumeDraftDialog, setShowResumeDraftDialog] = useState(false);
  const [showOverrideConfirm, setShowOverrideConfirm] = useState(false);
  const [showSaveConfirm, setShowSaveConfirm] = useState(false);

  const [activeDropdownKey, setActiveDropdownKey] = useState(null);
  const [nameSearchResults, setNameSearchResults] = useState([]);
  const [groupSearchResults, setGroupSearchResults] = useState([]);
  const [typeSearchResults, setTypeSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);

  const nameSearchTimeoutRef = useRef(null);
  const groupSearchTimeoutRef = useRef(null);
  const typeSearchTimeoutRef = useRef(null);

  // ── Auto-save draft ──────────────────────────────────────────────
  // Use refs for dirty/saving guards so they never trigger re-renders.
  const isDirtyRef = useRef(false);
  const isSavingDraftRef = useRef(false);
  const autoSaveIntervalRef = useRef(null);
  // isInitializedRef is set to true ONLY after fetchData fully completes and
  // the dirty flag has been reset. This prevents the initial state population
  // (setForm / setPlans inside handleLoad*) from being mistaken as user edits.
  const isInitializedRef = useRef(false);
  // "idle" | "saving" | "saved" | "dirty"
  const [autoSaveStatus, setAutoSaveStatus] = useState("idle");

  const toggleSectionCollapse = (phase, dayKey, sectionId) => {
    const key = `${phase}-${dayKey}-${sectionId}`;
    setCollapsedSections((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const isSectionOpen = (phase, dayKey, sectionId) => {
    const key = `${phase}-${dayKey}-${sectionId}`;
    return !collapsedSections[key];
  };

  const toggleWorkoutCard = (key) => {
    setActiveWorkoutKey((prev) => (prev === key ? "none" : key));
  };

  const isWorkoutCardOpen = (phase, dayKey, sectionId, index) => {
    const key = `${phase}-${dayKey}-${sectionId}-${index}`;
    if (activeWorkoutKey === "none") {
      return false;
    }
    if (activeWorkoutKey) {
      return activeWorkoutKey === key;
    }
    const isMainActiveDay =
      phase === activePhaseTab &&
      (dayKey === activeTab[activePhaseTab] ||
        (!enableDaySpecific[activePhaseTab] && dayKey === "common"));
    if (isMainActiveDay && index === 0) {
      return true;
    }
    return false;
  };
  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    const handleDocumentClick = (e) => {
      if (!e.target.closest(".search-container")) {
        setActiveDropdownKey(null);
      }
    };
    document.addEventListener("click", handleDocumentClick);
    return () => {
      document.removeEventListener("click", handleDocumentClick);
    };
  }, []);

  useEffect(() => {
    resizePlan("preparation", form.fitnessPreparationDays);
    resizePlan("active", activeDayCount);
    resizePlan("rest", restDayCount);
    resizePlan("repreparation", form.fitnessRePreparationDays);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    form.fitnessPreparationDays,
    form.fitnessRePreparationDays,
    activeDayCount,
    restDayCount,
  ]);

  // Mark dirty whenever the user edits form or plans.
  // Only fires after initialization is complete (isInitializedRef = true).
  // Also skips when reassignId is present — auto-save is only for new/draft cards.
  useEffect(() => {
    if (!isInitializedRef.current || reassignId) return;
    isDirtyRef.current = true;
    setAutoSaveStatus("dirty");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, plans]);

  const handleLoadExistingAssignment = (existingData, allBranchIds = []) => {
    if (!existingData) return;
    const card = existingData.workoutCard || {};
    const defaultBranchIds =
      allBranchIds.length > 0 ? allBranchIds : branches.map((b) => b.id);

    setForm({
      trainerId: existingData.trainerId || "none",
      startDate: existingData.startDate
        ? existingData.startDate.split("T")[0]
        : new Date().toISOString().split("T")[0],
      cardName: existingData.cardName || "",
      numberOfWeeks: existingData.numberOfWeeks || 6,
      fitnessPreparationDays: existingData.fitnessPreparationDays || 3,
      fitnessRePreparationDays: existingData.fitnessRePreparationDays || 3,
      restDays: existingData.restDays || ["sunday"],
      missedWorkoutPolicy:
        existingData.missedWorkoutPolicy || "skip_missed_workout",
      notes: existingData.notes || "",
      saveAsTemplate: false,
      templateName: "",
      branchIds: existingData.branchIds || defaultBranchIds,
      allowMemberToComplete: existingData.allowMemberToComplete || true,
      enableTimer: existingData.enableTimer || false,
      isFlexible: existingData.isFlexible || true,
    });

    setPlans({
      preparation: card.fitnessPreparation || {},
      repreparation: card.fitnessRePreparation || {},
      active: card.active || {},
      rest: card.rest || {},
      globalCommon:
        card.common && card.common.sections
          ? { common: card.common }
          : { common: makeDayPlan() },
      preparationCommon:
        card.fitnessPreparationCommon && card.fitnessPreparationCommon.sections
          ? { common: card.fitnessPreparationCommon }
          : { common: makeDayPlan() },
      repreparationCommon:
        card.fitnessRePreparationCommon &&
          card.fitnessRePreparationCommon.sections
          ? { common: card.fitnessRePreparationCommon }
          : { common: makeDayPlan() },
      activeCommon:
        card.activeCommon && card.activeCommon.sections
          ? { common: card.activeCommon }
          : { common: makeDayPlan() },
      restCommon:
        card.restCommon && card.restCommon.sections
          ? { common: card.restCommon }
          : { common: makeDayPlan() },
      preparationCommonPost:
        card.fitnessPreparationCommonPost &&
          card.fitnessPreparationCommonPost.sections
          ? { common: card.fitnessPreparationCommonPost }
          : { common: makeDayPlan() },
      repreparationCommonPost:
        card.fitnessRePreparationCommonPost &&
          card.fitnessRePreparationCommonPost.sections
          ? { common: card.fitnessRePreparationCommonPost }
          : { common: makeDayPlan() },
      activeCommonPost:
        card.activeCommonPost && card.activeCommonPost.sections
          ? { common: card.activeCommonPost }
          : { common: makeDayPlan() },
      restCommonPost:
        card.restCommonPost && card.restCommonPost.sections
          ? { common: card.restCommonPost }
          : { common: makeDayPlan() },
    });

    const savedEnabledTabs = card.enabledTabs || {};
    setEnabledTabs({
      preparation: savedEnabledTabs.hasOwnProperty("preparation")
        ? savedEnabledTabs.preparation
        : !!existingData.fitnessPreparationDays &&
        existingData.fitnessPreparationDays > 0,
      active: true,
      repreparation: savedEnabledTabs.hasOwnProperty("repreparation")
        ? savedEnabledTabs.repreparation
        : !!existingData.fitnessRePreparationDays &&
        existingData.fitnessRePreparationDays > 0,
      rest: savedEnabledTabs.hasOwnProperty("rest")
        ? savedEnabledTabs.rest
        : !!existingData.restDays && existingData.restDays.length > 0,
    });

    const savedEnableDaySpecific = card.enableDaySpecific || {};
    setEnableDaySpecific({
      preparation: savedEnableDaySpecific.hasOwnProperty("preparation")
        ? savedEnableDaySpecific.preparation
        : Object.keys(card.fitnessPreparation || {}).length > 0,
      repreparation: savedEnableDaySpecific.hasOwnProperty("repreparation")
        ? savedEnableDaySpecific.repreparation
        : Object.keys(card.fitnessRePreparation || {}).length > 0,
      rest: savedEnableDaySpecific.hasOwnProperty("rest")
        ? savedEnableDaySpecific.rest
        : Object.keys(card.rest || {}).length > 0,
    });
  };

  // ── performAutoSave ──────────────────────────────────────────────
  // Silently saves a draft in the background without interrupting the user.
  // Guards: template mode, reassign mode, nothing dirty, already saving, loading, no card name.
  const performAutoSave = useCallback(async () => {
    const isTemplateMode = id === "template";
    if (
      isTemplateMode ||
      reassignId ||
      !isDirtyRef.current ||
      isSavingDraftRef.current ||
      loading ||
      !form.cardName.trim()
    ) {
      return;
    }

    isSavingDraftRef.current = true;
    setAutoSaveStatus("saving");
    try {
      const cardPlans = await materializePlans(true);
      await api.post("/workouts/assignments", {
        memberId: id,
        trainerId:
          form.trainerId && form.trainerId !== "none" ? form.trainerId : null,
        startDate: form.startDate,
        cardName: form.cardName,
        numberOfWeeks: Number(form.numberOfWeeks),
        numberOfDays: Number(form.numberOfWeeks) * 7,
        fitnessPreparationDays: enabledTabs.preparation
          ? Number(form.fitnessPreparationDays)
          : 0,
        fitnessRePreparationDays: enabledTabs.repreparation
          ? Number(form.fitnessRePreparationDays)
          : 0,
        restDays: form.restDays || [],
        missedWorkoutPolicy: form.missedWorkoutPolicy,
        workoutCard: {
          fitnessPreparation:
            enabledTabs.preparation && enableDaySpecific.preparation
              ? cardPlans.preparation
              : {},
          fitnessRePreparation:
            enabledTabs.repreparation && enableDaySpecific.repreparation
              ? cardPlans.repreparation
              : {},
          active: enabledTabs.active ? cardPlans.active : {},
          rest:
            enabledTabs.rest && enableDaySpecific.rest ? cardPlans.rest : {},
          common: cardPlans.globalCommon,
          fitnessPreparationCommon: enabledTabs.preparation
            ? cardPlans.preparationCommon
            : {},
          fitnessRePreparationCommon: enabledTabs.repreparation
            ? cardPlans.repreparationCommon
            : {},
          activeCommon: enabledTabs.active ? cardPlans.activeCommon : {},
          restCommon: enabledTabs.rest ? cardPlans.restCommon : {},
          fitnessPreparationCommonPost: enabledTabs.preparation
            ? cardPlans.preparationCommonPost
            : {},
          fitnessRePreparationCommonPost: enabledTabs.repreparation
            ? cardPlans.repreparationCommonPost
            : {},
          activeCommonPost: enabledTabs.active
            ? cardPlans.activeCommonPost
            : {},
          restCommonPost: enabledTabs.rest ? cardPlans.restCommonPost : {},
          enabledTabs: enabledTabs,
          enableDaySpecific: enableDaySpecific,
        },
        notes: form.notes,
        allowMemberToComplete: form.allowMemberToComplete,
        enableTimer: form.enableTimer,
        isFlexible: form.isFlexible,
        saveAsTemplate: false,
        templateName: null,
        isDraft: true,
        branchIds: form.branchIds || [],
        reassignId: null,
        overrideCompleted: false,
      });
      isDirtyRef.current = false;
      setAutoSaveStatus("saved");
    } catch (_err) {
      // Silent failure — don't toast or disrupt the user.
      // Status reverts to dirty so the next interval will retry.
      setAutoSaveStatus("dirty");
    } finally {
      isSavingDraftRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, reassignId, loading, form, plans, enabledTabs, enableDaySpecific]);

  // Kick off the 30-second auto-save interval.
  useEffect(() => {
    autoSaveIntervalRef.current = setInterval(performAutoSave, 5_000);
    return () => clearInterval(autoSaveIntervalRef.current);
  }, [performAutoSave]);

  const fetchTemplatesForSearch = useCallback(async (query = "") => {
    setTemplateSearchLoading(true);
    try {
      const res = await api.get(`/workouts/templates`, {
        params: query ? { search: query } : {},
      });
      setTemplateList(res.data.templates || []);
    } catch (err) {
      toast.error("Failed to fetch templates");
    } finally {
      setTemplateSearchLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isTemplatePopoverOpen) {
      const delayDebounce = setTimeout(() => {
        fetchTemplatesForSearch(templateSearch);
      }, 300);
      return () => clearTimeout(delayDebounce);
    }
  }, [templateSearch, isTemplatePopoverOpen, fetchTemplatesForSearch]);

  const handleConfirmLoadTemplate = () => {
    if (!selectedTemplateToLoad) return;
    handleLoadExistingAssignment(selectedTemplateToLoad);
    toast.success(
      `Template "${selectedTemplateToLoad.name || selectedTemplateToLoad.cardName}" loaded successfully`,
    );
    setShowTemplateConfirmDialog(false);
    setIsTemplatePopoverOpen(false);
    setSelectedTemplateToLoad(null);
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const isTemplateMode = id === "template";
      const [memberRes, trainersRes, draftRes, branchesRes] = await Promise.all(
        [
          isTemplateMode
            ? Promise.resolve({
              data: { member: { name: "Template Mode", id: "template" } },
            })
            : api.get(`/members/${id}`),
          api.get("/workouts/trainers/list"),
          isTemplateMode
            ? Promise.resolve({ data: { assignment: null } })
            : api
              .get(`/workouts/assignments/draft/member/${id}`)
              .catch(() => ({ data: { assignment: null } })),
          api.get("/branches").catch(() => ({ data: { branches: [] } })),
        ],
      );
      setMember(memberRes.data.member || memberRes.data);
      setTrainers(trainersRes.data.trainers || []);
      const fetchedBranches =
        branchesRes.data.branches || branchesRes.data || [];
      setBranches(fetchedBranches);
      const allBranchIds = fetchedBranches.map((b) => b.id);

      const draft = draftRes.data.assignment;
      const resumeDraft = queryParams.get("resumeDraft") === "true";
      if (reassignId) {
        const reassignRes = await api.get(
          `/workouts/assignments/${reassignId}`,
        );
        const existingData = reassignRes.data.assignment;
        if (existingData) {
          handleLoadExistingAssignment(existingData, allBranchIds);
        }
      } else if (templateId) {
        const templateRes = await api.get(`/workouts/templates/${templateId}`);
        const existingData = templateRes.data.template;
        if (existingData) {
          handleLoadExistingAssignment(existingData, allBranchIds);
        }
      } else if (draft) {
        setDraftAssignment(draft);
        if (resumeDraft) {
          handleLoadDraft(draft, allBranchIds);
        } else {
          setShowResumeDraftDialog(true);
        }
      } else {
        updateForm({ branchIds: allBranchIds });
      }
    } catch (err) {
      toast.error(
        err.response?.data?.detail || "Failed to load workout card data",
      );
    } finally {
      setLoading(false);
      // Use a macrotask (setTimeout) so React has fully flushed all the
      // setForm / setPlans / resizePlan state updates triggered by handleLoad*
      // before we mark the builder as initialized and ready to track dirtiness.
      setTimeout(() => {
        isDirtyRef.current = false;
        isInitializedRef.current = true;
        setAutoSaveStatus("idle");
      }, 100);
    }
  };

  const handleLoadDraft = (customDraft, allBranchIds = []) => {
    const draftToLoad =
      customDraft && typeof customDraft === "object" && "id" in customDraft
        ? customDraft
        : draftAssignment;
    if (!draftToLoad) return;
    const card = draftToLoad.workoutCard || {};
    const defaultBranchIds =
      allBranchIds.length > 0 ? allBranchIds : branches.map((b) => b.id);

    setForm({
      trainerId: draftToLoad.trainerId || "none",
      startDate: draftToLoad.startDate
        ? draftToLoad.startDate.split("T")[0]
        : new Date().toISOString().split("T")[0],
      cardName: draftToLoad.cardName || "",
      numberOfWeeks: draftToLoad.numberOfWeeks || 4,
      fitnessPreparationDays: draftToLoad.fitnessPreparationDays || 3,
      fitnessRePreparationDays: draftToLoad.fitnessRePreparationDays || 3,
      restDays: draftToLoad.restDays || [],
      missedWorkoutPolicy:
        draftToLoad.missedWorkoutPolicy || "move_to_next_day",
      notes: draftToLoad.notes || "",
      saveAsTemplate: false,
      templateName: "",
      branchIds: draftToLoad.branchIds || defaultBranchIds,
      allowMemberToComplete: draftToLoad.allowMemberToComplete || true,
      enableTimer: draftToLoad.enableTimer || false,
      isFlexible: draftToLoad.isFlexible || true,
    });

    setPlans({
      preparation: card.fitnessPreparation || {},
      repreparation: card.fitnessRePreparation || {},
      active: card.active || {},
      rest: card.rest || {},
      globalCommon:
        card.common && card.common.sections
          ? { common: card.common }
          : { common: makeDayPlan() },
      preparationCommon:
        card.fitnessPreparationCommon && card.fitnessPreparationCommon.sections
          ? { common: card.fitnessPreparationCommon }
          : { common: makeDayPlan() },
      repreparationCommon:
        card.fitnessRePreparationCommon &&
          card.fitnessRePreparationCommon.sections
          ? { common: card.fitnessRePreparationCommon }
          : { common: makeDayPlan() },
      activeCommon:
        card.activeCommon && card.activeCommon.sections
          ? { common: card.activeCommon }
          : { common: makeDayPlan() },
      restCommon:
        card.restCommon && card.restCommon.sections
          ? { common: card.restCommon }
          : { common: makeDayPlan() },
      preparationCommonPost:
        card.fitnessPreparationCommonPost &&
          card.fitnessPreparationCommonPost.sections
          ? { common: card.fitnessPreparationCommonPost }
          : { common: makeDayPlan() },
      repreparationCommonPost:
        card.fitnessRePreparationCommonPost &&
          card.fitnessRePreparationCommonPost.sections
          ? { common: card.fitnessRePreparationCommonPost }
          : { common: makeDayPlan() },
      activeCommonPost:
        card.activeCommonPost && card.activeCommonPost.sections
          ? { common: card.activeCommonPost }
          : { common: makeDayPlan() },
      restCommonPost:
        card.restCommonPost && card.restCommonPost.sections
          ? { common: card.restCommonPost }
          : { common: makeDayPlan() },
    });

    const savedEnabledTabs = card.enabledTabs || {};
    setEnabledTabs({
      preparation: savedEnabledTabs.hasOwnProperty("preparation")
        ? savedEnabledTabs.preparation
        : !!draftToLoad.fitnessPreparationDays &&
        draftToLoad.fitnessPreparationDays > 0,
      active: true,
      repreparation: savedEnabledTabs.hasOwnProperty("repreparation")
        ? savedEnabledTabs.repreparation
        : !!draftToLoad.fitnessRePreparationDays &&
        draftToLoad.fitnessRePreparationDays > 0,
      rest: savedEnabledTabs.hasOwnProperty("rest")
        ? savedEnabledTabs.rest
        : !!draftToLoad.restDays && draftToLoad.restDays.length > 0,
    });

    const savedEnableDaySpecific = card.enableDaySpecific || {};
    setEnableDaySpecific({
      preparation: savedEnableDaySpecific.hasOwnProperty("preparation")
        ? savedEnableDaySpecific.preparation
        : Object.keys(card.fitnessPreparation || {}).length > 0,
      repreparation: savedEnableDaySpecific.hasOwnProperty("repreparation")
        ? savedEnableDaySpecific.repreparation
        : Object.keys(card.fitnessRePreparation || {}).length > 0,
      rest: savedEnableDaySpecific.hasOwnProperty("rest")
        ? savedEnableDaySpecific.rest
        : Object.keys(card.rest || {}).length > 0,
    });

    setShowResumeDraftDialog(false);
    toast.success("Draft loaded successfully");
  };

  const updateForm = (patch) => setForm((prev) => ({ ...prev, ...patch }));

  const resizePlan = (phase, count) => {
    setPlans((prev) => {
      const next = { ...prev[phase] };
      Array.from(
        { length: Math.max(1, count) },
        (_, index) => `day-${index + 1}`,
      ).forEach((key) => {
        if (!next[key]) next[key] = makeDayPlan();
      });
      Object.keys(next).forEach((key) => {
        const number = Number(key.replace("day-", ""));
        if (number > count) delete next[key];
      });
      return { ...prev, [phase]: next };
    });
    setActiveTab((prev) => {
      const currentTab = prev[phase];
      if (currentTab === "all-day") {
        return prev;
      }
      const dayNum = Number(currentTab?.replace("day-", "") || "");
      return {
        ...prev,
        [phase]: !isNaN(dayNum) && dayNum <= count ? currentTab : "all-day",
      };
    });
  };

  const updateDay = (phase, dayKey, updater) => {
    setPlans((prev) => ({
      ...prev,
      [phase]: {
        ...prev[phase],
        [dayKey]: updater(prev[phase][dayKey]),
      },
    }));
  };

  const updateSection = (phase, dayKey, sectionId, updater) => {
    updateDay(phase, dayKey, (day) => ({
      ...day,
      sections: day.sections.map((section) =>
        section.id === sectionId ? updater(section) : section,
      ),
    }));
  };

  const setWorkoutCount = (phase, dayKey, section, count) => {
    const nextCount = parseInt(count, 10);
    updateSection(phase, dayKey, section.id, (current) => {
      const workoutsForCount = Array.from(
        { length: nextCount },
        (_, index) => current.workouts[index] || makeWorkout(),
      );
      return {
        ...current,
        workoutCount: nextCount,
        workouts: workoutsForCount,
      };
    });
    const prefix = `${phase}-${dayKey}-${section.id}-`;
    if (activeWorkoutKey && activeWorkoutKey.startsWith(prefix)) {
      const activeIdx = parseInt(activeWorkoutKey.replace(prefix, ""), 10);
      if (activeIdx >= nextCount) {
        setActiveWorkoutKey(`${prefix}${nextCount - 1}`);
      }
    }
  };

  const addWorkout = (phase, dayKey, section) => {
    updateSection(phase, dayKey, section.id, (current) => ({
      ...current,
      workoutCount: current.workouts.length + 1,
      workouts: [...current.workouts, makeWorkout()],
    }));
    const newIndex = section.workouts?.length || 0;
    setActiveWorkoutKey(`${phase}-${dayKey}-${section.id}-${newIndex}`);
    setTimeout(() => {
      const inputEl = document.getElementById(
        `workout-input-${phase}-${dayKey}-${section.id}-${newIndex}`,
      );
      if (inputEl) {
        inputEl.scrollIntoView({ behavior: "smooth", block: "center" });
        inputEl.focus();
      }
    }, 150);
  };

  const pasteWorkout = (phase, dayKey, section) => {
    if (!copiedWorkout) return;
    const pasted = JSON.parse(JSON.stringify(copiedWorkout));
    pasted.clientId = crypto.randomUUID();

    updateSection(phase, dayKey, section.id, (current) => {
      const nextWorkouts = [...(current.workouts || []), pasted];
      return {
        ...current,
        workoutCount: nextWorkouts.length,
        workouts: nextWorkouts,
      };
    });

    const newIndex = section.workouts?.length || 0;
    const prefix = `${phase}-${dayKey}-${section.id}-`;
    setActiveWorkoutKey(`${prefix}${newIndex}`);
    toast.success(`Pasted workout "${pasted.title || "Untitled"}"`);
    setTimeout(() => {
      const inputEl = document.getElementById(
        `workout-input-${phase}-${dayKey}-${section.id}-${newIndex}`,
      );
      if (inputEl) {
        inputEl.scrollIntoView({ behavior: "smooth", block: "center" });
        inputEl.focus();
      }
    }, 150);
  };

  const pasteSection = (phase, dayKey) => {
    if (!copiedSection) return;
    const pasted = JSON.parse(JSON.stringify(copiedSection));
    pasted.id = crypto.randomUUID();
    pasted.workouts = (pasted.workouts || []).map((w) => ({
      ...w,
      clientId: crypto.randomUUID(),
    }));

    setPlans((prev) => {
      const day = prev[phase]?.[dayKey] || { sections: [], notes: "" };
      const nextSections = [...(day.sections || []), pasted];
      return {
        ...prev,
        [phase]: {
          ...prev[phase],
          [dayKey]: {
            ...day,
            sections: nextSections,
          },
        },
      };
    });

    toast.success(`Pasted section "${pasted.name || "Untitled"}"`);
  };

  const handleWorkoutNameSearch = (
    phase,
    dayKey,
    section,
    workoutIndex,
    text,
  ) => {
    const cardKey = `${phase}-${dayKey}-${section.id}-${workoutIndex}`;
    updateWorkout(phase, dayKey, section, workoutIndex, {
      title: text,
      workoutId: "",
    });
    setActiveDropdownKey(`name-${cardKey}`);

    if (nameSearchTimeoutRef.current)
      clearTimeout(nameSearchTimeoutRef.current);

    if (text.trim().length < 3) {
      setNameSearchResults([]);
      return;
    }

    nameSearchTimeoutRef.current = setTimeout(async () => {
      try {
        setIsSearching(true);
        const res = await api.get(
          `/workouts/search-all?search=${encodeURIComponent(text)}`,
        );
        setNameSearchResults(res.data.workouts || []);
      } catch (err) {
        console.error(err);
      } finally {
        setIsSearching(false);
      }
    }, 300);
  };

  const handleWorkoutNameFocus = (
    phase,
    dayKey,
    section,
    workoutIndex,
    text,
  ) => {
    const cardKey = `${phase}-${dayKey}-${section.id}-${workoutIndex}`;
    setActiveDropdownKey(`name-${cardKey}`);

    if (nameSearchTimeoutRef.current)
      clearTimeout(nameSearchTimeoutRef.current);

    if (text.trim().length < 3) {
      setNameSearchResults([]);
      return;
    }

    nameSearchTimeoutRef.current = setTimeout(async () => {
      try {
        setIsSearching(true);
        const res = await api.get(
          `/workouts/search-all?search=${encodeURIComponent(text)}`,
        );
        setNameSearchResults(res.data.workouts || []);
      } catch (err) {
        console.error(err);
      } finally {
        setIsSearching(false);
      }
    }, 100);
  };

  const handleGroupSearch = (phase, dayKey, section, workoutIndex, text) => {
    const cardKey = `${phase}-${dayKey}-${section.id}-${workoutIndex}`;
    updateWorkout(phase, dayKey, section, workoutIndex, { groupName: text });
    setActiveDropdownKey(`group-${cardKey}`);

    if (groupSearchTimeoutRef.current)
      clearTimeout(groupSearchTimeoutRef.current);
    groupSearchTimeoutRef.current = setTimeout(async () => {
      try {
        const res = await api.get(
          `/workouts/groups?search=${encodeURIComponent(text)}`,
        );
        setGroupSearchResults(res.data.groups || []);
      } catch (err) {
        console.error(err);
      }
    }, 300);
  };

  const handleGroupFocus = (phase, dayKey, section, workoutIndex, text) => {
    const cardKey = `${phase}-${dayKey}-${section.id}-${workoutIndex}`;
    setActiveDropdownKey(`group-${cardKey}`);

    if (groupSearchTimeoutRef.current)
      clearTimeout(groupSearchTimeoutRef.current);
    groupSearchTimeoutRef.current = setTimeout(async () => {
      try {
        const res = await api.get(
          `/workouts/groups?search=${encodeURIComponent(text)}`,
        );
        setGroupSearchResults(res.data.groups || []);
      } catch (err) {
        console.error(err);
      }
    }, 100);
  };

  const handleTypeSearch = (phase, dayKey, section, workoutIndex, text) => {
    const cardKey = `${phase}-${dayKey}-${section.id}-${workoutIndex}`;
    updateWorkout(phase, dayKey, section, workoutIndex, { workoutType: text });
    setActiveDropdownKey(`type-${cardKey}`);

    if (typeSearchTimeoutRef.current)
      clearTimeout(typeSearchTimeoutRef.current);
    typeSearchTimeoutRef.current = setTimeout(async () => {
      try {
        const res = await api.get(
          `/workouts/types?search=${encodeURIComponent(text)}`,
        );
        setTypeSearchResults(res.data.types || []);
      } catch (err) {
        console.error(err);
      }
    }, 300);
  };

  const handleTypeFocus = (phase, dayKey, section, workoutIndex, text) => {
    const cardKey = `${phase}-${dayKey}-${section.id}-${workoutIndex}`;
    setActiveDropdownKey(`type-${cardKey}`);

    if (typeSearchTimeoutRef.current)
      clearTimeout(typeSearchTimeoutRef.current);
    typeSearchTimeoutRef.current = setTimeout(async () => {
      try {
        const res = await api.get(
          `/workouts/types?search=${encodeURIComponent(text)}`,
        );
        setTypeSearchResults(res.data.types || []);
      } catch (err) {
        console.error(err);
      }
    }, 100);
  };

  const removeWorkout = (phase, dayKey, section, workoutIndex) => {
    updateSection(phase, dayKey, section.id, (current) => {
      const workoutsForSection = current.workouts.filter(
        (_, index) => index !== workoutIndex,
      );
      return {
        ...current,
        workoutCount: workoutsForSection.length,
        workouts: workoutsForSection,
      };
    });
    const prefix = `${phase}-${dayKey}-${section.id}-`;
    if (activeWorkoutKey && activeWorkoutKey.startsWith(prefix)) {
      const activeIdx = parseInt(activeWorkoutKey.replace(prefix, ""), 10);
      if (activeIdx === workoutIndex) {
        const newActiveIndex = Math.max(0, workoutIndex - 1);
        setActiveWorkoutKey(`${prefix}${newActiveIndex}`);
      } else if (activeIdx > workoutIndex) {
        setActiveWorkoutKey(`${prefix}${activeIdx - 1}`);
      }
    }
  };

  const moveWorkout = (phase, dayKey, section, fromIndex, direction) => {
    updateSection(phase, dayKey, section.id, (current) => {
      const toIndex = fromIndex + direction;
      if (toIndex < 0 || toIndex >= current.workouts.length) return current;
      const next = [...current.workouts];
      const [item] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, item);
      return { ...current, workouts: next };
    });
    const prefix = `${phase}-${dayKey}-${section.id}-`;
    const fromKey = `${prefix}${fromIndex}`;
    const toIndex = fromIndex + direction;
    const toKey = `${prefix}${toIndex}`;
    if (activeWorkoutKey === fromKey) {
      setActiveWorkoutKey(toKey);
    } else if (activeWorkoutKey === toKey) {
      setActiveWorkoutKey(fromKey);
    }
  };

  const moveSection = (phase, dayKey, sectionIndex, direction) => {
    updateDay(phase, dayKey, (day) => {
      const toIndex = sectionIndex + direction;
      if (toIndex < 0 || toIndex >= day.sections.length) return day;
      const next = [...day.sections];
      const [item] = next.splice(sectionIndex, 1);
      next.splice(toIndex, 0, item);
      return { ...day, sections: next };
    });
  };

  const updateWorkout = (phase, dayKey, section, workoutIndex, patch) => {
    updateSection(phase, dayKey, section.id, (current) => ({
      ...current,
      workouts: current.workouts.map((workout, index) =>
        index === workoutIndex ? { ...workout, ...patch } : workout,
      ),
    }));
  };

  const duplicateWorkout = (phase, dayKey, section, workoutIndex) => {
    updateSection(phase, dayKey, section.id, (current) => {
      const workoutToDuplicate = current.workouts[workoutIndex];
      if (!workoutToDuplicate) return current;
      const duplicatedWorkout = JSON.parse(JSON.stringify(workoutToDuplicate));
      duplicatedWorkout.clientId = crypto.randomUUID();
      const nextWorkouts = [...current.workouts];
      nextWorkouts.splice(workoutIndex + 1, 0, duplicatedWorkout);
      return {
        ...current,
        workoutCount: nextWorkouts.length,
        workouts: nextWorkouts,
      };
    });
    const nextIndex = workoutIndex + 1;
    const prefix = `${phase}-${dayKey}-${section.id}-`;
    setActiveWorkoutKey(`${prefix}${nextIndex}`);
    toast.success("Workout duplicated");
    setTimeout(() => {
      const inputEl = document.getElementById(
        `workout-input-${phase}-${dayKey}-${section.id}-${nextIndex}`,
      );
      if (inputEl) {
        inputEl.scrollIntoView({ behavior: "smooth", block: "center" });
        inputEl.focus();
      }
    }, 150);
  };

  const addSection = (phase, dayKey) => {
    setAddSectionDialog({ phase, dayKey });
    setNewSectionName("");
  };

  const handleConfirmAddSection = () => {
    if (!newSectionName.trim() || !addSectionDialog) return;
    const { phase, dayKey } = addSectionDialog;
    updateDay(phase, dayKey, (day) => ({
      ...day,
      sections: [...day.sections, makeSection(newSectionName.trim())],
    }));
    setAddSectionDialog(null);
    setNewSectionName("");
  };

  const addSectionWithName = (phase, dayKey, name) => {
    if (!name?.trim()) return;
    updateDay(phase, dayKey, (day) => ({
      ...day,
      sections: [...day.sections, makeSection(name.trim())],
    }));
  };

  const handleNavigateToWorkouts = () => {
    if (!form.cardName.trim()) {
      setCardNameError(true);
      toast.error("Name of the Card is required");
      const el = document.getElementById("cardName-input");
      if (el) el.focus();
      return;
    }
    setCardNameError(false);
    setActiveStep("workouts");
  };

  const removeSection = (phase, dayKey, sectionId) => {
    updateDay(phase, dayKey, (day) => ({
      ...day,
      sections: day.sections.filter((section) => section.id !== sectionId),
    }));
  };

  const getCopySources = (targetPhase, targetDayKey) => {
    const sources = [];

    const addSource = (srcPhase, srcDayKey, label) => {
      if (srcPhase === targetPhase && srcDayKey === targetDayKey) return;
      const dayPlan = plans[srcPhase]?.[srcDayKey];
      if (!dayPlan || !dayPlan.sections) return;

      const workoutCount = dayPlan.sections.reduce(
        (sum, s) => sum + (s.workouts?.length || 0),
        0,
      );
      if (workoutCount > 0) {
        sources.push({
          phase: srcPhase,
          dayKey: srcDayKey,
          label: `${label} (${workoutCount} Workout${workoutCount !== 1 ? "s" : ""})`,
        });
      }
    };

    addSource("globalCommon", "common", "Global Common");

    if (enabledTabs.preparation) {
      addSource("preparationCommon", "common", "Prep Common (Pre-Workout)");
      addSource(
        "preparationCommonPost",
        "common",
        "Prep Common (Post-Workout)",
      );
      if (enableDaySpecific.preparation) {
        const prepDaysCount = Number(form.fitnessPreparationDays) || 0;
        for (let i = 1; i <= prepDaysCount; i++) {
          addSource("preparation", `day-${i}`, `Prep Day ${i}`);
        }
      } else {
        addSource("preparation", "day-1", "Prep Day 1");
      }
    }

    addSource("activeCommon", "common", "Active Common (Pre-Workout)");
    addSource("activeCommonPost", "common", "Active Common (Post-Workout)");
    const activeDaysCount = activeDayCount;
    for (let i = 1; i <= activeDaysCount; i++) {
      addSource("active", `day-${i}`, `Active Day ${i}`);
    }

    if (enabledTabs.repreparation) {
      addSource(
        "repreparationCommon",
        "common",
        "Re-Prep Common (Pre-Workout)",
      );
      addSource(
        "repreparationCommonPost",
        "common",
        "Re-Prep Common (Post-Workout)",
      );
      if (enableDaySpecific.repreparation) {
        const rePrepDaysCount = Number(form.fitnessRePreparationDays) || 0;
        for (let i = 1; i <= rePrepDaysCount; i++) {
          addSource("repreparation", `day-${i}`, `Re-Prep Day ${i}`);
        }
      } else {
        addSource("repreparation", "day-1", "Re-Prep Day 1");
      }
    }

    if (enabledTabs.rest) {
      addSource("restCommon", "common", "Rest Common (Pre-Workout)");
      addSource("restCommonPost", "common", "Rest Common (Post-Workout)");
      if (enableDaySpecific.rest) {
        const restDaysCount = restDayCount;
        for (let i = 1; i <= restDaysCount; i++) {
          addSource("rest", `day-${i}`, `Rest Day ${i}`);
        }
      } else {
        addSource("rest", "day-1", "Rest Day 1");
      }
    }

    return sources;
  };

  const copyDayPlan = (
    targetPhase,
    targetDayKey,
    sourcePhase,
    sourceDayKey,
    sourceLabel,
    mode = "replace",
  ) => {
    setPlans((prev) => {
      const sourcePhasePlans = prev[sourcePhase];
      if (!sourcePhasePlans) return prev;
      const sourceDay = sourcePhasePlans[sourceDayKey];
      if (!sourceDay) return prev;

      const targetPhasePlans = prev[targetPhase];
      const targetDay = targetPhasePlans?.[targetDayKey] || {
        sections: [],
        notes: "",
      };

      let nextSections = [];
      let nextNotes = targetDay.notes || "";

      if (mode === "replace") {
        nextSections = (sourceDay.sections || []).map((section) => {
          const newSectionId = crypto.randomUUID();
          return {
            ...section,
            id: newSectionId,
            workouts: (section.workouts || []).map((workout) => ({
              ...workout,
              clientId: crypto.randomUUID(),
            })),
          };
        });
        nextNotes = sourceDay.notes || "";
      } else {
        // Mode is "append"
        // 1. Copy target sections to keep existing state
        nextSections = (targetDay.sections || []).map((s) => ({
          ...s,
          workouts: [...(s.workouts || [])],
        }));

        // 2. Map and append source sections
        (sourceDay.sections || []).forEach((srcSection) => {
          const existingTargetSection = nextSections.find(
            (tSection) =>
              tSection.name?.toLowerCase().trim() ===
              srcSection.name?.toLowerCase().trim(),
          );

          if (existingTargetSection) {
            // Append workouts to existing section
            const newWorkouts = (srcSection.workouts || []).map((workout) => ({
              ...workout,
              clientId: crypto.randomUUID(),
            }));
            existingTargetSection.workouts = [
              ...existingTargetSection.workouts,
              ...newWorkouts,
            ];
            existingTargetSection.workoutCount =
              existingTargetSection.workouts.length;
          } else {
            // Append new section entirely
            const newSectionId = crypto.randomUUID();
            nextSections.push({
              ...srcSection,
              id: newSectionId,
              workouts: (srcSection.workouts || []).map((workout) => ({
                ...workout,
                clientId: crypto.randomUUID(),
              })),
              workoutCount: (srcSection.workouts || []).length,
            });
          }
        });

        // 3. Concatenate notes if source has notes and it doesn't already contain it
        if (sourceDay.notes?.trim()) {
          if (nextNotes.trim()) {
            if (!nextNotes.includes(sourceDay.notes.trim())) {
              nextNotes = `${nextNotes}\n${sourceDay.notes}`;
            }
          } else {
            nextNotes = sourceDay.notes;
          }
        }
      }

      return {
        ...prev,
        [targetPhase]: {
          ...prev[targetPhase],
          [targetDayKey]: {
            ...targetDay,
            sections: nextSections,
            notes: nextNotes,
          },
        },
      };
    });

    setActiveWorkoutKey("");
    toast.success(
      mode === "replace"
        ? `Replaced plan with workouts from ${sourceLabel}`
        : `Appended workouts from ${sourceLabel}`,
    );
  };

  const materializePlans = async (isDraft = false) => {
    const nextPlans = {};

    const materializeSinglePlan = async (day) => {
      const nextDay = {
        ...day,
        sections: [],
      };
      const sections = day && Array.isArray(day.sections) ? day.sections : [];
      for (const section of sections) {
        const nextSection = { ...section, workouts: [] };
        const workoutsList =
          section && Array.isArray(section.workouts) ? section.workouts : [];
        for (const workout of workoutsList) {
          nextSection.workouts.push({
            ...normalizeWorkoutFields(workout),
            workoutId: workout.workoutId || "",
          });
        }
        nextDay.sections.push(nextSection);
      }
      return nextDay;
    };

    for (const phase of ["preparation", "repreparation", "active", "rest"]) {
      nextPlans[phase] = {};
      for (const [dayKey, day] of Object.entries(plans[phase])) {
        nextPlans[phase][dayKey] = await materializeSinglePlan(day);
      }
    }

    for (const phase of [
      "globalCommon",
      "preparationCommon",
      "repreparationCommon",
      "activeCommon",
      "restCommon",
      "preparationCommonPost",
      "repreparationCommonPost",
      "activeCommonPost",
      "restCommonPost",
    ]) {
      if (plans[phase] && plans[phase].common) {
        nextPlans[phase] = await materializeSinglePlan(plans[phase].common);
      }
    }

    return nextPlans;
  };

  const executeSubmit = async (isDraft = false, overrideCompleted = false) => {
    if (!form.cardName.trim()) {
      setCardNameError(true);
      toast.error("Card name is required");
      const el = document.getElementById("cardName-input");
      if (el) el.focus();
      return;
    }
    if (templateId) {
      setSaving(true);
      try {
        const cardPlans = await materializePlans(isDraft);
        const payload = {
          name: form.cardName,
          cardName: form.cardName,
          numberOfWeeks: Number(form.numberOfWeeks),
          fitnessPreparationDays: enabledTabs.preparation
            ? Number(form.fitnessPreparationDays)
            : 0,
          fitnessRePreparationDays: enabledTabs.repreparation
            ? Number(form.fitnessRePreparationDays)
            : 0,
          restDays: form.restDays || [],
          missedWorkoutPolicy: form.missedWorkoutPolicy,
          workoutCard: {
            fitnessPreparation:
              enabledTabs.preparation && enableDaySpecific.preparation
                ? cardPlans.preparation
                : {},
            fitnessRePreparation:
              enabledTabs.repreparation && enableDaySpecific.repreparation
                ? cardPlans.repreparation
                : {},
            active: enabledTabs.active ? cardPlans.active : {},
            rest:
              enabledTabs.rest && enableDaySpecific.rest ? cardPlans.rest : {},
            common: cardPlans.globalCommon,
            fitnessPreparationCommon: enabledTabs.preparation
              ? cardPlans.preparationCommon
              : {},
            fitnessRePreparationCommon: enabledTabs.repreparation
              ? cardPlans.repreparationCommon
              : {},
            activeCommon: enabledTabs.active ? cardPlans.activeCommon : {},
            restCommon: enabledTabs.rest ? cardPlans.restCommon : {},
            fitnessPreparationCommonPost: enabledTabs.preparation
              ? cardPlans.preparationCommonPost
              : {},
            fitnessRePreparationCommonPost: enabledTabs.repreparation
              ? cardPlans.repreparationCommonPost
              : {},
            activeCommonPost: enabledTabs.active
              ? cardPlans.activeCommonPost
              : {},
            restCommonPost: enabledTabs.rest ? cardPlans.restCommonPost : {},
            enabledTabs: enabledTabs,
            enableDaySpecific: enableDaySpecific,
          },
          notes: form.notes,
          isFlexible: form.isFlexible,
          branchIds: form.branchIds || [],
        };
        await api.put(`/workouts/templates/${templateId}`, payload);
        toast.success("Template updated successfully");
        navigate("/workouts?tab=templates");
      } catch (err) {
        toast.error("Failed to update template");
      } finally {
        setSaving(false);
      }
      return;
    }
    if (!isDraft && form.saveAsTemplate && !form.templateName.trim()) {
      toast.error("Template name is required");
      return;
    }
    setSaving(true);
    try {
      const cardPlans = await materializePlans(isDraft);
      await api.post("/workouts/assignments", {
        memberId: id,
        trainerId:
          form.trainerId && form.trainerId !== "none" ? form.trainerId : null,
        startDate: form.startDate,
        cardName: form.cardName,
        numberOfWeeks: Number(form.numberOfWeeks),
        numberOfDays: Number(form.numberOfWeeks) * 7,
        fitnessPreparationDays: enabledTabs.preparation
          ? Number(form.fitnessPreparationDays)
          : 0,
        fitnessRePreparationDays: enabledTabs.repreparation
          ? Number(form.fitnessRePreparationDays)
          : 0,
        restDays: form.restDays || [],
        missedWorkoutPolicy: form.missedWorkoutPolicy,
        workoutCard: {
          fitnessPreparation:
            enabledTabs.preparation && enableDaySpecific.preparation
              ? cardPlans.preparation
              : {},
          fitnessRePreparation:
            enabledTabs.repreparation && enableDaySpecific.repreparation
              ? cardPlans.repreparation
              : {},
          active: enabledTabs.active ? cardPlans.active : {},
          rest:
            enabledTabs.rest && enableDaySpecific.rest ? cardPlans.rest : {},
          common: cardPlans.globalCommon,
          fitnessPreparationCommon: enabledTabs.preparation
            ? cardPlans.preparationCommon
            : {},
          fitnessRePreparationCommon: enabledTabs.repreparation
            ? cardPlans.repreparationCommon
            : {},
          activeCommon: enabledTabs.active ? cardPlans.activeCommon : {},
          restCommon: enabledTabs.rest ? cardPlans.restCommon : {},
          fitnessPreparationCommonPost: enabledTabs.preparation
            ? cardPlans.preparationCommonPost
            : {},
          fitnessRePreparationCommonPost: enabledTabs.repreparation
            ? cardPlans.repreparationCommonPost
            : {},
          activeCommonPost: enabledTabs.active
            ? cardPlans.activeCommonPost
            : {},
          restCommonPost: enabledTabs.rest ? cardPlans.restCommonPost : {},
          enabledTabs: enabledTabs,
          enableDaySpecific: enableDaySpecific,
        },
        notes: form.notes,
        allowMemberToComplete: form.allowMemberToComplete,
        enableTimer: form.enableTimer,
        isFlexible: form.isFlexible,
        saveAsTemplate: !isDraft && form.saveAsTemplate,
        templateName: !isDraft ? form.templateName : null,
        isDraft,
        branchIds: form.branchIds || [],
        reassignId: reassignId || null,
        overrideCompleted,
      });
      toast.success(
        isDraft ? "Draft saved successfully" : "Workout card assigned",
      );
      if (!isDraft) {
        navigate(`/members/${id}?tab=workout`);
      }
    } catch (err) {
      console.log(err);

      toast.error(err.response?.data?.detail || "Failed to save workout card");
    } finally {
      setSaving(false);
      setShowOverrideConfirm(false);
    }
  };

  const handleSubmit = async (isDraft = false) => {
    if (isDraft) {
      // Sync the auto-save status with the manual save action.
      setAutoSaveStatus("saving");
      await executeSubmit(true, false);
      isDirtyRef.current = false;
      setAutoSaveStatus("saved");
    } else {
      updateForm({ saveAsTemplate: false, templateName: "" });
      setShowSaveConfirm(true);
    }
  };

  const renderPlanFields = (phase, dayKey, day) => (
    <div className="space-y-2">
      <div className="flex justify-end items-center gap-1 md:gap-2 pt-2 flex-wrap">
        {(() => {
          const copySources = getCopySources(phase, dayKey);
          if (copySources.length === 0) return null;
          return (
            <TooltipProvider>
              <Tooltip delayDuration={300}>
                <TooltipTrigger asChild>
                  <span>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button type="button" variant="outline" size="sm">
                          <Copy className="h-4 w-4" />
                          <span className="flex gap-1">
                            Copy Plan
                            <span className="hidden md:block">From</span>
                          </span>
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent
                        align="end"
                        className="max-h-64 overflow-y-auto"
                      >
                        {copySources.map((source) => (
                          <DropdownMenuItem
                            key={`${source.phase}-${source.dayKey}`}
                            onClick={() => {
                              const targetDayPlan = plans[phase]?.[dayKey];
                              const targetWorkoutsCount =
                                targetDayPlan?.sections?.reduce(
                                  (sum, s) => sum + (s.workouts?.length || 0),
                                  0,
                                ) || 0;
                              const sourceName = source.label.split(" (")[0];

                              if (targetWorkoutsCount > 0) {
                                setCopyPlanConfirm({
                                  targetPhase: phase,
                                  targetDayKey: dayKey,
                                  sourcePhase: source.phase,
                                  sourceDayKey: source.dayKey,
                                  sourceLabel: sourceName,
                                });
                              } else {
                                copyDayPlan(
                                  phase,
                                  dayKey,
                                  source.phase,
                                  source.dayKey,
                                  sourceName,
                                  "replace",
                                );
                              }
                            }}
                          >
                            {source.label}
                          </DropdownMenuItem>
                        ))}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </span>
                </TooltipTrigger>
                <TooltipContent>
                  Copy sections, workouts and notes from another day or common
                  plan
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          );
        })()}
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => addSection(phase, dayKey)}
        >
          <Plus className="h-4 w-4" />
          <span className="flex gap-1">
            <span className="hidden md:block">Add</span> Workout Section
          </span>
        </Button>
        {copiedSection && (
          <div className="flex items-center gap-1">
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="text-primary border-primary/40 bg-primary/5 hover:bg-primary/10 hover:border-primary/80"
              onClick={() => pasteSection(phase, dayKey)}
            >
              <ClipboardPaste className="h-4 w-4" />
              Paste Section ({copiedSection.name || "Untitled"})
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-9 w-9 text-muted-foreground hover:text-destructive shrink-0"
              onClick={() => {
                setCopiedSection(null);
                toast.success("Section clipboard cleared");
              }}
              title="Clear copied section"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        )}
      </div>
      {day.sections.length === 0 ? (
        <div className="flex flex-col items-center justify-center p-8 border border-dashed border-border rounded-xl bg-secondary/5 space-y-4 text-center my-2 transition-all duration-300">
          <button
            type="button"
            onClick={() => addSection(phase, dayKey)}
            className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center text-primary hover:bg-primary/20 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary/40 focus:ring-offset-2 cursor-pointer"
            title="Add workout section"
            aria-label="Add workout section"
          >
            <Plus className="h-6 w-6" />
          </button>
          <div className="space-y-1">
            <h3 className="font-heading text-sm font-semibold uppercase tracking-wider text-foreground">
              No sections in this day
            </h3>
            <p className="text-xs text-muted-foreground max-w-sm">
              Quickly start by adding one of these common sections, or click
              above to add a custom section.
            </p>
          </div>
          <div className="flex flex-wrap gap-2 justify-center max-w-lg">
            {(() => {
              let suggestions = [];
              if (phase.includes("Common")) {
                suggestions = [
                  "Warmup",
                  "Cooldown",
                  "Cardio",
                  "Stretching",
                  "Mobility",
                ];
              } else if (phase === "active") {
                suggestions = [
                  "Push Workout",
                  "Pull Workout",
                  "Leg Workout",
                  "Upper Body",
                  "Lower Body",
                  "Core Workout",
                ];
              } else if (phase === "preparation") {
                suggestions = [
                  "Conditioning",
                  "Full Body",
                  "General Prep (GPP)",
                  "Light Cardio",
                ];
              } else if (phase === "repreparation") {
                suggestions = [
                  "Reconditioning",
                  "Recovery Strength",
                  "Mobility Flow",
                ];
              } else if (phase === "rest") {
                suggestions = [
                  "Active Recovery",
                  "Yoga/Stretching",
                  "Mobility Flow",
                  "Light Walk",
                ];
              } else {
                suggestions = ["Warmup", "Cooldown"];
              }

              return suggestions.map((name) => (
                <Button
                  key={name}
                  type="button"
                  variant="secondary"
                  size="sm"
                  className="text-xs font-semibold"
                  onClick={() => addSectionWithName(phase, dayKey, name)}
                >
                  <Plus className="h-3.5 w-3.5 mr-1" /> {name}
                </Button>
              ));
            })()}
          </div>
        </div>
      ) : (
        day.sections.map((section, sectionIndex) => {
          const isSectionExpanded = isSectionOpen(phase, dayKey, section.id);
          return (
            <div
              key={section.id}
              className="rounded-lg border border-border bg-background/50 overflow-hidden"
            >
              <div className="flex  gap-1 md:gap-2 border-b border-border px-2 py-2 md:px-3 md:py-3  flex-wrap md:items-center justify-end bg-secondary/5">
                <div className="flex items-center gap-1 md:gap-2  flex-1">
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-muted-foreground shrink-0"
                    onClick={() =>
                      toggleSectionCollapse(phase, dayKey, section.id)
                    }
                  >
                    {isSectionExpanded ? (
                      <ChevronDown className="h-4 w-4" />
                    ) : (
                      <ChevronRight className="h-4 w-4" />
                    )}
                  </Button>
                  <Input
                    value={section.name}
                    onChange={(event) =>
                      updateSection(phase, dayKey, section.id, (current) => ({
                        ...current,
                        name: event.target.value,
                      }))
                    }
                    className="h-9 w-24 md:w-full md:max-w-[220px] font-heading text-sm uppercase"
                  />
                  {/* <div className="text-[9px] hidden sm:flex font-semibold text-muted-foreground bg-secondary/30 px-3 py-0.5 rounded-full border border-border/40 select-none">
                  <span className="flex items-center gap-2">
                    {`${section.workouts?.length || 0} ${
                      (section.workouts?.length || 0) === 1
                        ? "Workout"
                        : "Workouts"
                    }`}
                  </span>
                </div> */}
                  <TooltipProvider>
                    <Tooltip delayDuration={300}>
                      <TooltipTrigger asChild>
                        <span>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            disabled={false}
                            onClick={() => {
                              if (
                                section.workouts &&
                                section.workouts.length > 0
                              ) {
                                setDeleteSectionConfirm({
                                  phase,
                                  dayKey,
                                  sectionId: section.id,
                                  sectionName:
                                    section.name || "Workout Section",
                                  workoutCount: section.workouts.length,
                                });
                              } else {
                                removeSection(phase, dayKey, section.id);
                              }
                            }}
                            aria-label="Delete section"
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </span>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p className="text-xs">{"Delete"}</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>

                  <TooltipProvider>
                    <Tooltip delayDuration={300}>
                      <TooltipTrigger asChild>
                        <span>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            onClick={() => {
                              setCopiedSection(
                                JSON.parse(JSON.stringify(section)),
                              );
                              toast.success(
                                `Copied section "${section.name || "Workout Section"}" to clipboard`,
                              );
                            }}
                            aria-label="Copy section to clipboard"
                            className="md:w-auto md:px-2 md:gap-1.5"
                          >
                            <ClipboardCopy className="h-4 w-4" />
                            <span className="hidden md:inline text-xs font-semibold uppercase tracking-wider">
                              Copy
                            </span>
                          </Button>
                        </span>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p className="text-xs">Copy Section</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>

                  <TooltipProvider>
                    <Tooltip delayDuration={300}>
                      <TooltipTrigger asChild>
                        <span>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            disabled={sectionIndex === 0}
                            onClick={() =>
                              moveSection(phase, dayKey, sectionIndex, -1)
                            }
                            aria-label="Move section up"
                          >
                            <ArrowUp className="h-4 w-4" />
                          </Button>
                        </span>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p className="text-xs">Move Section Up</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>

                  <TooltipProvider>
                    <Tooltip delayDuration={300}>
                      <TooltipTrigger asChild>
                        <span>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            disabled={sectionIndex === day.sections.length - 1}
                            onClick={() =>
                              moveSection(phase, dayKey, sectionIndex, 1)
                            }
                            aria-label="Move section down"
                          >
                            <ArrowDown className="h-4 w-4" />
                          </Button>
                        </span>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p className="text-xs">Move Section Down</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
                <div className="flex items-center gap-2 ">
                  <Label className="text-[9x] font-heading uppercase text-muted-foreground min-w-[375px]:hidden flex">
                    No. of <span className="hidden sm:block">Workout</span>
                  </Label>
                  <Select
                    value={String(section.workoutCount)}
                    onValueChange={(value) =>
                      setWorkoutCount(phase, dayKey, section, value)
                    }
                  >
                    <SelectTrigger className="w-20 bg-background">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Array.from(
                        { length: phase !== "active" ? 21 : 20 },
                        (_, index) => (phase !== "active" ? index : index + 1),
                      ).map((countOption) => (
                        <SelectItem
                          key={countOption}
                          value={String(countOption)}
                        >
                          {countOption}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {isSectionExpanded && (
                <div className="space-y-3 p-3">
                  {section.workouts.map((workout, workoutIndex) => {
                    const cardKey = `${phase}-${dayKey}-${section.id}-${workoutIndex}`;
                    const isOpen = isWorkoutCardOpen(
                      phase,
                      dayKey,
                      section.id,
                      workoutIndex,
                    );

                    return (
                      <div
                        key={workout.clientId || workoutIndex}
                        className="rounded-lg border border-border bg-secondary/70 p-3 space-y-1.5 transition-all duration-200"
                      >
                        <div
                          className="flex items-center flex-wrap  justify-between gap-2 cursor-pointer select-none"
                          onClick={() => toggleWorkoutCard(cardKey)}
                        >
                          <div className="flex items-center gap-2">
                            {isOpen ? (
                              <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" />
                            ) : (
                              <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
                            )}
                            <span className="font-heading text-sm font-semibold text-card-foreground">
                              <span className="text-muted-foreground mr-1.5 font-normal text-xs">
                                #{workoutIndex + 1}
                              </span>
                              {workout.title || `Workout ${workoutIndex + 1}`}
                            </span>
                          </div>
                          <div
                            className="flex items-center gap-1"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <TooltipProvider>
                              <Tooltip delayDuration={300}>
                                <TooltipTrigger asChild>
                                  <span>
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="icon"
                                      disabled={workoutIndex === 0}
                                      onClick={() =>
                                        moveWorkout(
                                          phase,
                                          dayKey,
                                          section,
                                          workoutIndex,
                                          -1,
                                        )
                                      }
                                      aria-label="Move workout up"
                                    >
                                      <ArrowUp className="h-4 w-4" />
                                    </Button>
                                  </span>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p className="text-xs">Move Up</p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>

                            <TooltipProvider>
                              <Tooltip delayDuration={300}>
                                <TooltipTrigger asChild>
                                  <span>
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="icon"
                                      disabled={
                                        workoutIndex ===
                                        section.workouts.length - 1
                                      }
                                      onClick={() =>
                                        moveWorkout(
                                          phase,
                                          dayKey,
                                          section,
                                          workoutIndex,
                                          1,
                                        )
                                      }
                                      aria-label="Move workout down"
                                    >
                                      <ArrowDown className="h-4 w-4" />
                                    </Button>
                                  </span>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p className="text-xs">Move Down</p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>

                            <TooltipProvider>
                              <Tooltip delayDuration={300}>
                                <TooltipTrigger asChild>
                                  <span>
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="icon"
                                      onClick={() =>
                                        duplicateWorkout(
                                          phase,
                                          dayKey,
                                          section,
                                          workoutIndex,
                                        )
                                      }
                                      aria-label="Duplicate workout"
                                      className="md:w-auto md:px-2 md:gap-1.5"
                                    >
                                      <Copy className="h-4 w-4" />
                                      <span className="hidden md:inline text-xs font-semibold uppercase tracking-wider">
                                        Duplicate
                                      </span>
                                    </Button>
                                  </span>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p className="text-xs font-semibold">
                                    Duplicate workout
                                  </p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>

                            <TooltipProvider>
                              <Tooltip delayDuration={300}>
                                <TooltipTrigger asChild>
                                  <span>
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="icon"
                                      onClick={() => {
                                        const workoutToCopy =
                                          section.workouts[workoutIndex];
                                        if (workoutToCopy) {
                                          setCopiedWorkout(
                                            JSON.parse(
                                              JSON.stringify(workoutToCopy),
                                            ),
                                          );
                                          toast.success(
                                            `Copied "${workoutToCopy.title || "Workout"}" to clipboard`,
                                          );
                                        }
                                      }}
                                      aria-label="Copy workout to clipboard"
                                      className="md:w-auto md:px-2 md:gap-1.5"
                                    >
                                      <ClipboardCopy className="h-4 w-4" />
                                      <span className="hidden md:inline text-xs font-semibold uppercase tracking-wider">
                                        Copy
                                      </span>
                                    </Button>
                                  </span>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p className="text-xs font-semibold">
                                    Copy workout to paste on the same day, a
                                    different day, or in a different section
                                  </p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>

                            <TooltipProvider>
                              <Tooltip delayDuration={300}>
                                <TooltipTrigger asChild>
                                  <span>
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="icon"
                                      disabled={false}
                                      onClick={() =>
                                        setDeleteWorkoutConfirm({
                                          phase,
                                          dayKey,
                                          section,
                                          workoutIndex,
                                          workoutTitle:
                                            workout.title ||
                                            `Workout ${workoutIndex + 1}`,
                                        })
                                      }
                                      aria-label="Delete workout"
                                    >
                                      <Trash2 className="h-4 w-4 text-destructive" />
                                    </Button>
                                  </span>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p className="text-xs">{"Delete"}</p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          </div>
                        </div>

                        {isOpen && (
                          <div className="space-y-3 pt-2 border-t border-border/40 mt-1">
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                              <div className="space-y-2 search-container relative">
                                <Label className="text-xs font-heading uppercase">
                                  Name of the Workout
                                </Label>
                                <Input
                                  id={`workout-input-${phase}-${dayKey}-${section.id}-${workoutIndex}`}
                                  value={workout.title || ""}
                                  onChange={(event) =>
                                    handleWorkoutNameSearch(
                                      phase,
                                      dayKey,
                                      section,
                                      workoutIndex,
                                      event.target.value,
                                    )
                                  }
                                  onFocus={(event) =>
                                    handleWorkoutNameFocus(
                                      phase,
                                      dayKey,
                                      section,
                                      workoutIndex,
                                      event.target.value,
                                    )
                                  }
                                  placeholder="Search or type new workout"
                                  autoComplete="off"
                                />
                                {activeDropdownKey ===
                                  `name-${phase}-${dayKey}-${section.id}-${workoutIndex}` && (
                                    <div className="absolute left-0 top-[calc(100%+4px)] min-w-full md:w-[250px] max-w-[calc(100vw-130px)] sm:max-w-none bg-card border border-border rounded-lg shadow-xl z-[100] max-h-60 overflow-y-auto divide-y divide-border/40 scrollbar-thin">
                                      {(workout.title || "").trim().length < 3 ? (
                                        <div className="p-3 text-xs text-muted-foreground text-center bg-card font-medium">
                                          Type 3 or more characters to start
                                          searching...
                                        </div>
                                      ) : isSearching &&
                                        nameSearchResults.length === 0 ? (
                                        <div className="p-3 text-xs text-muted-foreground text-center flex items-center justify-center gap-2 bg-card">
                                          <div className="h-3 w-3 animate-spin rounded-full border border-primary border-t-transparent" />
                                          Searching workouts...
                                        </div>
                                      ) : nameSearchResults.length === 0 ? (
                                        <div className="p-3 text-xs text-muted-foreground text-center bg-card">
                                          No matching workouts. Press enter or
                                          type to create new.
                                        </div>
                                      ) : (
                                        nameSearchResults.map((item) => {
                                          const setsCount =
                                            item.sets ||
                                            item.setDetails?.length ||
                                            0;
                                          const metric =
                                            item.workoutMetric || "no metric";

                                          return (
                                            <div
                                              key={item.id}
                                              className=" px-2 py-2 hover:bg-secondary/40 cursor-pointer transition-colors text-left bg-card"
                                              onClick={() => {
                                                updateWorkout(
                                                  phase,
                                                  dayKey,
                                                  section,
                                                  workoutIndex,
                                                  {
                                                    ...normalizeWorkoutFields(
                                                      item,
                                                    ),
                                                    title: item.title,
                                                    workoutId: item.workoutId,
                                                    groupName:
                                                      item.groupNames?.[0] || "",
                                                    workoutType:
                                                      item.workoutType || "",
                                                  },
                                                );
                                                setActiveDropdownKey(null);
                                              }}
                                            >
                                              {/* Line 1: Title */}
                                              <div className="font-semibold text-sm text-foreground truncate flex items-center justify-between">
                                                <span>{item.title}</span>
                                                {item.isPreset && (
                                                  <span className="text-[9px] bg-amber-500/10 text-amber-500 border border-amber-500/20 px-1 rounded ml-2 select-none uppercase font-bold">
                                                    Preset
                                                  </span>
                                                )}
                                              </div>

                                              {/* Line 1.5: Preset Name */}
                                              {item.isPreset &&
                                                item.presetName && (
                                                  <div className="text-xs text-amber-500/90 font-medium mt-0.5 truncate">
                                                    {item.presetName}
                                                  </div>
                                                )}

                                              {/* Line 2: Chips */}
                                              <div className="flex flex-wrap gap-1 mt-1">
                                                {item.groupNames?.[0] && (
                                                  <span className="bg-primary/10 text-primary border border-primary/20 text-[9px] font-semibold uppercase tracking-wider px-1 rounded select-none">
                                                    {item.groupNames[0]}
                                                  </span>
                                                )}
                                                {item.workoutType && (
                                                  <span className="bg-secondary text-secondary-foreground border border-border/50 text-[9px] font-semibold uppercase tracking-wider px-1 rounded select-none">
                                                    {item.workoutType}
                                                  </span>
                                                )}
                                                {item.difficulty && (
                                                  <span
                                                    className={`text-[9px] font-semibold uppercase tracking-wider px-1.5  rounded select-none border ${item.difficulty.toLowerCase() ===
                                                      "beginner"
                                                      ? "bg-green-500/10 text-green-500 border-green-500/20"
                                                      : item.difficulty.toLowerCase() ===
                                                        "intermediate"
                                                        ? "bg-amber-500/10 text-amber-500 border-amber-500/20"
                                                        : "bg-red-500/10 text-red-500 border-red-500/20"
                                                      }`}
                                                  >
                                                    {item.difficulty}
                                                  </span>
                                                )}
                                              </div>

                                              {/* Line 3: Metrics & Description details */}
                                              <div className="text-[10px] text-muted-foreground mt-1 truncate">
                                                <span className="font-medium text-foreground">
                                                  {setsCount} set
                                                  {setsCount !== 1 ? "s" : ""} (
                                                  {metric})
                                                </span>
                                                {(item.shortDescription ||
                                                  item.instructions) && (
                                                    <span className="opacity-80">
                                                      {" • "}
                                                      {item.shortDescription ||
                                                        item.instructions}
                                                    </span>
                                                  )}
                                              </div>
                                            </div>
                                          );
                                        })
                                      )}
                                    </div>
                                  )}
                              </div>
                              <div className="space-y-2 search-container relative">
                                <Label className="text-xs font-heading uppercase">
                                  Workout Group
                                </Label>
                                <Input
                                  value={workout.groupName || ""}
                                  onChange={(event) =>
                                    handleGroupSearch(
                                      phase,
                                      dayKey,
                                      section,
                                      workoutIndex,
                                      event.target.value,
                                    )
                                  }
                                  onFocus={(event) =>
                                    handleGroupFocus(
                                      phase,
                                      dayKey,
                                      section,
                                      workoutIndex,
                                      event.target.value,
                                    )
                                  }
                                  placeholder="Search or type new group"
                                  autoComplete="off"
                                />
                                {activeDropdownKey ===
                                  `group-${phase}-${dayKey}-${section.id}-${workoutIndex}` &&
                                  groupSearchResults.length > 0 && (
                                    <div className="absolute left-0 right-0 top-[calc(100%+4px)] bg-card border border-border rounded-lg shadow-xl z-[100] max-h-48 overflow-y-auto divide-y divide-border/40 scrollbar-thin">
                                      {groupSearchResults.map((group) => (
                                        <div
                                          key={group.id}
                                          className="p-2.5 hover:bg-secondary/40 cursor-pointer transition-colors text-left text-sm text-foreground truncate bg-card"
                                          onClick={() => {
                                            updateWorkout(
                                              phase,
                                              dayKey,
                                              section,
                                              workoutIndex,
                                              { groupName: group.name },
                                            );
                                            setActiveDropdownKey(null);
                                          }}
                                        >
                                          {group.name}
                                        </div>
                                      ))}
                                    </div>
                                  )}
                              </div>
                              <div className="space-y-2 search-container relative">
                                <Label className="text-xs font-heading uppercase">
                                  Workout Type
                                </Label>
                                <Input
                                  value={workout.workoutType || ""}
                                  onChange={(event) =>
                                    handleTypeSearch(
                                      phase,
                                      dayKey,
                                      section,
                                      workoutIndex,
                                      event.target.value,
                                    )
                                  }
                                  onFocus={(event) =>
                                    handleTypeFocus(
                                      phase,
                                      dayKey,
                                      section,
                                      workoutIndex,
                                      event.target.value,
                                    )
                                  }
                                  placeholder="Search or type new type"
                                  autoComplete="off"
                                />
                                {activeDropdownKey ===
                                  `type-${phase}-${dayKey}-${section.id}-${workoutIndex}` &&
                                  typeSearchResults.length > 0 && (
                                    <div className="absolute left-0 right-0 top-[calc(100%+4px)] bg-card border border-border rounded-lg shadow-xl z-[100] max-h-48 overflow-y-auto divide-y divide-border/40 scrollbar-thin">
                                      {typeSearchResults.map((type) => (
                                        <div
                                          key={type.id}
                                          className="p-2.5 hover:bg-secondary/40 cursor-pointer transition-colors text-left text-sm text-foreground truncate bg-card"
                                          onClick={() => {
                                            updateWorkout(
                                              phase,
                                              dayKey,
                                              section,
                                              workoutIndex,
                                              { workoutType: type.name },
                                            );
                                            setActiveDropdownKey(null);
                                          }}
                                        >
                                          {type.name}
                                        </div>
                                      ))}
                                    </div>
                                  )}
                              </div>
                            </div>
                            <DetailedWorkoutFields
                              value={workout}
                              onChange={(next) =>
                                updateWorkout(
                                  phase,
                                  dayKey,
                                  section,
                                  workoutIndex,
                                  next,
                                )
                              }
                            />
                          </div>
                        )}
                      </div>
                    );
                  })}

                  <div className="flex flex-col sm:flex-row gap-2 mt-2 pt-2 border-t border-border/10">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="flex-1 border-dashed border-border hover:border-primary/50 flex items-center justify-center gap-1.5 h-10 transition-all duration-200 py-2 bg-secondary/50"
                      onClick={() => addWorkout(phase, dayKey, section)}
                    >
                      <Plus className="h-4 w-4 text-muted-foreground" />
                      Add Workout
                    </Button>

                    {copiedWorkout && (
                      <div className="flex-1 flex items-center gap-1">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="flex-1 border-dashed border-primary/40 hover:border-primary/80 text-primary bg-primary/5 hover:bg-primary/10 flex items-center justify-center gap-1.5 h-10 transition-all duration-200"
                          onClick={() => pasteWorkout(phase, dayKey, section)}
                        >
                          <ClipboardPaste className="h-4 w-4" />
                          Paste Copied Workout (
                          {copiedWorkout.title || "Untitled"})
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          className="border-dashed border-border text-muted-foreground hover:text-destructive hover:border-destructive/40 h-10 w-10 shrink-0 transition-all duration-200"
                          onClick={() => {
                            setCopiedWorkout(null);
                            toast.success("Workout clipboard cleared");
                          }}
                          title="Clear copied workout"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t border-border/40">
        {/* <div className="space-y-2">
          <Label className="text-xs font-heading uppercase text-muted-foreground">
            Day Name / Focus (e.g. Push, Chest, Legs)
          </Label>
          <Input
            value={day.label || ""}
            onChange={(event) =>
              updateDay(phase, dayKey, (current) => ({
                ...current,
                label: event.target.value,
              }))
            }
            className="h-10 border-border/50 focus-visible:ring-primary/30"
            placeholder="Focus name e.g. Push, Pull, Legs..."
          />
        </div> */}
        <div className="space-y-2">
          <Label className="text-xs font-heading uppercase text-muted-foreground">
            Notes for This Workout Session
          </Label>
          <Textarea
            value={day.notes || ""}
            onChange={(event) =>
              updateDay(phase, dayKey, (current) => ({
                ...current,
                notes: event.target.value,
              }))
            }
            rows={1}
            className="min-h-[40px] h-10 resize-none py-2 border-border/50 focus-visible:ring-primary/30"
            placeholder="General notes for this session..."
          />
        </div>
      </div>
    </div>
  );

  const renderDayTabs = (phase, label, count) => {
    const isDaySpecificEnabled =
      !["preparation", "repreparation", "rest"].includes(phase) ||
      enableDaySpecific[phase];

    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between gap-3 pt-2">
          <div>
            <h2 className="font-heading text-lg uppercase tracking-wide">
              {label}
            </h2>
            {isDaySpecificEnabled && (
              <p className="text-xs text-muted-foreground">
                {count} day workout{count !== 1 ? "s" : ""}
              </p>
            )}
          </div>
        </div>

        {/* Optional Day-Specific Workouts Toggle */}
        {["preparation", "repreparation", "rest"].includes(phase) && (
          <div className="flex items-center justify-between gap-3 rounded-lg border border-border bg-secondary/10 px-4 py-3 shadow-sm hover:bg-secondary/20 transition-all duration-200">
            <div className="space-y-0.5">
              <h4 className="text-sm font-semibold uppercase tracking-wide">
                Customize Workouts by Day
              </h4>
              <p className="text-xs text-muted-foreground">
                Enable to specify different workouts for each day. Otherwise,
                only common workouts will apply.
              </p>
            </div>
            <Switch
              checked={enableDaySpecific[phase]}
              onCheckedChange={(checked) => {
                setEnableDaySpecific((prev) => ({ ...prev, [phase]: checked }));
                if (!checked) {
                  setActiveTab((prev) => ({ ...prev, [phase]: "all-day" }));
                }
              }}
            />
          </div>
        )}

        <Tabs
          value={activeTab[phase] || "all-day"}
          onValueChange={(value) => {
            setActiveTab((prev) => ({ ...prev, [phase]: value }));
            setActiveWorkoutKey("");
          }}
        >
          <TabsList className="flex h-auto flex-nowrap overflow-x-auto justify-start border-border border-t border-r border-l border-b bg-card rounded-t-xl rounded-b-none px-4 pt-3 pb-0 gap-6 items-center scrollbar-hide overflow-y-hidden">
            <TabsTrigger
              value="all-day"
              className="flex items-center gap-2 px-1 pb-3 pt-1 border-b-2 border-transparent rounded-none text-xs font-heading font-semibold uppercase tracking-wider transition-all duration-200 hover:text-foreground data-[state=active]:text-primary data-[state=active]:border-primary data-[state=active]:shadow-none data-[state=inactive]:bg-transparent data-[state=inactive]:text-muted-foreground -mb-[1px] whitespace-nowrap"
            >
              <Dumbbell className="h-4 w-4 shrink-0 text-muted-foreground/60" />
              All Day (Pre & Post)
            </TabsTrigger>

            {isDaySpecificEnabled &&
              Array.from({ length: count }, (_, index) => {
                const dayKey = `day-${index + 1}`;
                const dayPlan = plans[phase][dayKey];
                const allWorkouts =
                  dayPlan?.sections?.flatMap(
                    (section) => section.workouts || [],
                  ) || [];

                let tabState = "empty"; // "empty" | "writing" | "done"
                if (allWorkouts.length > 0) {
                  const isComplete = allWorkouts.every(
                    (workout) =>
                      (workout.title || "").trim() || workout.workoutId,
                  );
                  tabState = isComplete ? "done" : "writing";
                }

                return (
                  <TabsTrigger
                    key={dayKey}
                    value={dayKey}
                    className={cn(
                      "flex items-center gap-2 px-1 pb-3 pt-1 border-b-2 border-transparent rounded-none text-xs font-heading font-semibold uppercase tracking-wider transition-all duration-200 hover:text-foreground data-[state=active]:shadow-none data-[state=inactive]:bg-transparent -mb-[1px] whitespace-nowrap",
                      tabState === "done" &&
                      "text-emerald-600 dark:text-emerald-500 hover:text-emerald-700 data-[state=active]:text-emerald-600 data-[state=active]:border-emerald-600 dark:data-[state=active]:text-emerald-400 dark:data-[state=active]:border-emerald-400 data-[state=inactive]:text-emerald-600/75 dark:data-[state=inactive]:text-emerald-500/75 font-semibold",
                      tabState === "writing" &&
                      "text-amber-600 dark:text-amber-500 hover:text-amber-700 data-[state=active]:text-amber-600 data-[state=active]:border-amber-600 dark:data-[state=active]:text-amber-400 dark:data-[state=active]:border-amber-400 data-[state=inactive]:text-amber-600/75 dark:data-[state=inactive]:text-amber-500/75 font-semibold",
                      tabState === "empty" &&
                      "data-[state=active]:text-primary data-[state=active]:border-primary data-[state=inactive]:text-muted-foreground",
                    )}
                  >
                    {tabState === "done" && (
                      <CheckCircle2
                        className="h-4.5 w-4.5 text-emerald-500 fill-emerald-500/10 shrink-0"
                        strokeWidth={2.5}
                      />
                    )}
                    {tabState === "writing" && (
                      <Pencil
                        className="h-4 w-4 text-amber-500 shrink-0"
                        strokeWidth={2.2}
                      />
                    )}
                    {tabState === "empty" && (
                      <Circle className="h-4 w-4 text-muted-foreground/60 shrink-0" />
                    )}
                    Day {index + 1}
                  </TabsTrigger>
                );
              })}
          </TabsList>

          <TabsContent
            value="all-day"
            className="space-y-4 bg-card border border-border border-t-0 rounded-b-xl p-3 md:p-4 mt-0 rounded-t-none text-left"
          >
            {/* Common or Pre-Workout */}
            <div className="rounded-lg border border-border bg-card shadow-sm overflow-hidden">
              <div
                className="flex items-center justify-between px-2 py-1.5 cursor-pointer select-none hover:bg-secondary/5 transition-all duration-200"
                onClick={() =>
                  setShowPhaseCommon((prev) => ({
                    ...prev,
                    [phase]: !prev[phase],
                  }))
                }
              >
                <div className="flex items-center gap-2.5">
                  {showPhaseCommon[phase] ? (
                    <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" />
                  ) : (
                    <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
                  )}
                  <div className="flex flex-col md:flex-row md:items-center gap-1.5">
                    <h3 className="font-heading text-xs md:text-base font-semibold uppercase tracking-wide text-card-foreground">
                      Common or Pre-Workout
                    </h3>
                    <p className="text-[10px] md:text-xs text-muted-foreground">
                      (Applied to all days in this tab)
                    </p>
                  </div>
                </div>
                {(() => {
                  const commonPlan = plans[phase + "Common"]?.common;
                  const commonWorkoutsCount =
                    commonPlan?.sections?.reduce(
                      (sum, s) => sum + (s.workouts?.length || 0),
                      0,
                    ) || 0;
                  return (
                    <div className="text-[10px] sm:text-xs font-semibold text-muted-foreground bg-secondary/30 px-3 py-0.5 rounded-full border border-border/40 select-none">
                      <span className="flex items-center gap-2">
                        {commonWorkoutsCount === 1
                          ? "1 Workout"
                          : `${commonWorkoutsCount} Workouts`}
                      </span>
                    </div>
                  );
                })()}
              </div>
              {showPhaseCommon[phase] && (
                <div className="border-t border-border p-1.5 md:p-4 !pt-0">
                  {renderPlanFields(
                    phase + "Common",
                    "common",
                    plans[phase + "Common"]?.common || makeDayPlan(),
                  )}
                </div>
              )}
            </div>

            {/* Common or Post-Workout */}
            <div className="rounded-lg border border-border bg-card shadow-sm overflow-hidden">
              <div
                className="flex items-center justify-between px-2 py-1.5 cursor-pointer select-none hover:bg-secondary/5 transition-all duration-200"
                onClick={() =>
                  setShowPhaseCommonPost((prev) => ({
                    ...prev,
                    [phase]: !prev[phase],
                  }))
                }
              >
                <div className="flex items-center gap-2.5">
                  {showPhaseCommonPost[phase] ? (
                    <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" />
                  ) : (
                    <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
                  )}
                  <div className="flex flex-col md:flex-row md:items-center gap-1.5">
                    <h3 className="font-heading text-xs md:text-base font-semibold uppercase tracking-wide text-card-foreground">
                      Common or Post-Workout
                    </h3>
                    <p className="text-[10px] md:text-xs text-muted-foreground">
                      (Applied to all days in this tab)
                    </p>
                  </div>
                </div>
                {(() => {
                  const commonPlan = plans[phase + "CommonPost"]?.common;
                  const commonWorkoutsCount =
                    commonPlan?.sections?.reduce(
                      (sum, s) => sum + (s.workouts?.length || 0),
                      0,
                    ) || 0;
                  return (
                    <div className="text-[10px] sm:text-xs font-semibold text-muted-foreground bg-secondary/30 px-3 py-0.5 rounded-full border border-border/40 select-none">
                      <span className="flex items-center gap-2">
                        {commonWorkoutsCount === 1
                          ? "1 Workout"
                          : `${commonWorkoutsCount} Workouts`}
                      </span>
                    </div>
                  );
                })()}
              </div>
              {showPhaseCommonPost[phase] && (
                <div className="border-t border-border p-1.5 md:p-4 !pt-0">
                  {renderPlanFields(
                    phase + "CommonPost",
                    "common",
                    plans[phase + "CommonPost"]?.common || makeDayPlan(),
                  )}
                </div>
              )}
            </div>
          </TabsContent>

          {isDaySpecificEnabled &&
            Object.entries(plans[phase]).map(([dayKey, day]) => (
              <TabsContent
                key={dayKey}
                value={dayKey}
                className="space-y-3 bg-card border border-border border-t-0 rounded-b-xl p-2 pt-0 mt-0 rounded-t-none "
              >
                {renderPlanFields(phase, dayKey, day)}
              </TabsContent>
            ))}
        </Tabs>
      </div>
    );
  };

  const renderConfiguration = () => (
    <div className="space-y-6 text-left">
      <div className="rounded-lg border border-border bg-card p-4 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="space-y-2 md:col-span-2">
            <Label className="text-xs font-heading uppercase">
              Name of the Card
            </Label>
            <Input
              id="cardName-input"
              value={form.cardName}
              onChange={(event) => {
                updateForm({ cardName: event.target.value });
                if (event.target.value.trim()) {
                  setCardNameError(false);
                }
              }}
              placeholder="Member transformation plan"
              className={cn(
                cardNameError &&
                "border-destructive focus-visible:ring-destructive/30",
              )}
            />
            {cardNameError && (
              <p className="text-xs text-destructive mt-1">
                Name of the Card is required
              </p>
            )}
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-heading uppercase">
              No. of Weeks
            </Label>
            <Select
              value={String(form.numberOfWeeks)}
              onValueChange={(value) =>
                updateForm({ numberOfWeeks: Number(value) })
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Array.from({ length: 12 }, (_, index) => index + 1).map(
                  (week) => (
                    <SelectItem key={week} value={String(week)}>
                      {week}
                    </SelectItem>
                  ),
                )}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-heading uppercase">
              Workout Start Date
            </Label>
            <Input
              type="date"
              value={form.startDate}
              onChange={(event) =>
                updateForm({ startDate: event.target.value })
              }
            />
          </div>

          <div className="space-y-2 md:col-span-2">
            <Label className="text-xs font-heading uppercase">
              Missed Workout Policy
            </Label>
            <Select
              value={form.missedWorkoutPolicy}
              onValueChange={(value) =>
                updateForm({ missedWorkoutPolicy: value })
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {MISSED_POLICIES.map((policy) => (
                  <SelectItem key={policy.value} value={policy.value}>
                    {policy.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2 md:col-span-2">
            <Label className="text-xs font-heading uppercase">
              Assign Trainer
            </Label>
            <Select
              value={form.trainerId}
              onValueChange={(value) => updateForm({ trainerId: value })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No Trainer</SelectItem>
                {trainers.map((trainer) => (
                  <SelectItem key={trainer.id} value={trainer.id}>
                    {trainer.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="space-y-2">
          <Label className="text-xs font-heading uppercase">Rest Days</Label>
          <div className="grid grid-cols-4 gap-1.5">
            {DAYS.map((day) => (
              <label
                key={day.key}
                className="flex items-center gap-1 rounded-lg border border-border bg-secondary/20 px-2 py-1.5 text-xs cursor-pointer hover:bg-secondary/35 transition-all duration-200"
              >
                <Checkbox
                  checked={form.restDays.includes(day.key)}
                  onCheckedChange={(checked) => {
                    const nextRestDays = checked
                      ? [...form.restDays, day.key]
                      : form.restDays.filter((key) => key !== day.key);

                    if (checked) {
                      const newActiveDayCount = Math.max(
                        1,
                        7 - nextRestDays.length,
                      );
                      const currentActiveDayCount = activeDayCount;

                      if (newActiveDayCount < currentActiveDayCount) {
                        const daysWithWorkouts = [];
                        for (
                          let d = newActiveDayCount + 1;
                          d <= currentActiveDayCount;
                          d++
                        ) {
                          const dayKey = `day-${d}`;
                          const dayPlan = plans.active[dayKey];
                          const hasWorkouts = dayPlan?.sections?.some(
                            (section) =>
                              section.workouts && section.workouts.length > 0,
                          );
                          if (hasWorkouts) {
                            daysWithWorkouts.push(d);
                          }
                        }

                        if (daysWithWorkouts.length > 0) {
                          setRestDayConfirm({
                            restDays: nextRestDays,
                            daysWithWorkouts,
                          });
                          return;
                        }
                      }
                    }

                    updateForm({ restDays: nextRestDays });
                  }}
                />
                {day.label}
              </label>
            ))}
          </div>
        </div>
        {branches.length > 0 && (
          <div className="space-y-2">
            <Label className="text-xs font-heading uppercase flex items-center gap-1.5">
              <Building2 className="h-3.5 w-3.5" /> Assign Branches
            </Label>
            <MultiSelect
              options={branches.map((b) => ({ label: b.name, value: b.id }))}
              selected={form.branchIds || []}
              onChange={(vals) => updateForm({ branchIds: vals })}
              placeholder="Select branches..."
            />
          </div>
        )}
        <div className="space-y-3 pt-2">
          <div className="space-y-2">
            <Label className="text-xs font-heading uppercase">
              Notes for Workout Card
            </Label>
            <Textarea
              value={form.notes}
              onChange={(event) => updateForm({ notes: event.target.value })}
              rows={3}
            />
          </div>
          <div className="rounded-lg border border-border bg-secondary/20 p-3 space-y-3">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-sm font-medium">Allow member to complete</p>
                <p className="text-xs text-muted-foreground">
                  Allow member to check off exercises and complete workouts.
                </p>
              </div>
              <Switch
                checked={form.allowMemberToComplete}
                onCheckedChange={(allowMemberToComplete) =>
                  updateForm({ allowMemberToComplete })
                }
              />
            </div>
          </div>
          { }
          <div className="rounded-lg border border-border bg-secondary/20 p-3 space-y-3">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-sm font-medium">Timer Options</p>
                <p className="text-xs text-muted-foreground">
                  Show a timer for exercises in the member portal.
                </p>
              </div>
              <Switch
                checked={form.enableTimer}
                onCheckedChange={(enableTimer) => updateForm({ enableTimer })}
              />
            </div>
          </div>
          <div className="rounded-lg border border-border bg-secondary/20 p-3 space-y-3">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-sm font-medium">Flexible Workout Options</p>
                <p className="text-xs text-muted-foreground">
                  Allow members to choose any assigned workout day from the
                  dropdown list.
                </p>
              </div>
              <Switch
                checked={form.isFlexible}
                onCheckedChange={(isFlexible) => updateForm({ isFlexible })}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="space-y-5 pb-8">
      <div className="sticky top-0 z-30 flex flex-col gap-3 border-b border-border py-4 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 md:flex-row md:items-center md:justify-between">
        <div className="flex items-center gap-3">
          <Button
            type="button"
            variant="outline"
            size="icon"
            onClick={() =>
              navigate(
                templateId
                  ? "/workouts?tab=templates"
                  : `/members/${id}?tab=workout`,
              )
            }
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          {templateId ? (
            <div className="h-11 w-11 rounded-full bg-primary/10 flex items-center justify-center text-primary">
              <Dumbbell className="h-5 w-5" />
            </div>
          ) : (
            <ClickableAvatar
              src={member?.photo}
              name={member?.name}
              className="h-11 w-11"
              fallbackClassName="bg-primary/10 text-primary text-xs font-heading font-bold"
            />
          )}
          <div>
            <h1 className="font-heading text-xl font-bold uppercase tracking-wide">
              {templateId ? "Edit Workout Card Template" : "Add Workout Card"}
            </h1>
            <p className="text-sm text-muted-foreground">
              {templateId ? "Template Mode" : member?.name}
            </p>
          </div>
        </div>
        <div className="flex  gap-2 flex-wrap justify-between md:justify-end">
          <Popover
            open={isTemplatePopoverOpen}
            onOpenChange={setIsTemplatePopoverOpen}
          >
            <PopoverTrigger asChild>
              <Button type="button" variant="outline" className="px-2 gap-0">
                <FolderOpen className="h-4 w-4 mr-2" />
                <span className="">Load Templates</span>
              </Button>
            </PopoverTrigger>
            <PopoverContent
              className="w-80 p-0 bg-card border-border shadow-md rounded-lg overflow-hidden"
              align="end"
            >
              <div className="p-3 border-b border-border bg-secondary/10">
                <div className="relative">
                  <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search templates..."
                    value={templateSearch}
                    onChange={(e) => setTemplateSearch(e.target.value)}
                    className="pl-9 h-9"
                  />
                </div>
              </div>
              <div className="max-h-60 overflow-y-auto p-1.5 space-y-1">
                {templateSearchLoading ? (
                  <div className="flex items-center justify-center py-6 text-sm text-muted-foreground gap-2">
                    <Loader2 className="h-4 w-4 animate-spin text-primary" />
                    Searching templates...
                  </div>
                ) : templateList.length === 0 ? (
                  <div className="text-center py-6 text-sm text-muted-foreground">
                    No templates found
                  </div>
                ) : (
                  templateList.map((t) => (
                    <button
                      key={t.id}
                      type="button"
                      onClick={() => {
                        setSelectedTemplateToLoad(t);
                        setShowTemplateConfirmDialog(true);
                      }}
                      className="w-full text-left px-3 py-2 text-sm rounded hover:bg-secondary/50 transition-colors focus:bg-secondary/50 focus:outline-none"
                    >
                      <div className="font-semibold text-foreground truncate">
                        {t.name || t.cardName}
                      </div>
                      {t.notes && (
                        <div className="text-xs text-muted-foreground truncate">
                          {t.notes}
                        </div>
                      )}
                      <div className="text-[10px] text-muted-foreground mt-0.5">
                        {t.numberOfWeeks} Weeks •{" "}
                        {Math.max(1, 7 - (t.restDays?.length || 0))} Active Days
                      </div>
                    </button>
                  ))
                )}
              </div>
            </PopoverContent>
          </Popover>

          {!templateId && !reassignId && (
            <div className="flex flex-col gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => handleSubmit(true)}
                disabled={saving}
                className="px-2 gap-0"
              >
                <Save className="h-4 w-4 mr-2" />
                {saving ? "Saving..." : "Save Draft"}
              </Button>
              <div className="flex items-center gap-1 text-xs text-muted-foreground">
                {/* Auto-save status indicator — only for new/draft workout cards */}
                {autoSaveStatus === "saving" && (
                  <span className="hidden sm:flex items-center gap-1 text-xs text-muted-foreground">
                    <Loader2 className="h-3 w-3 animate-spin" />
                    Saving…
                  </span>
                )}
                {autoSaveStatus === "saved" && (
                  <span className="hidden sm:flex items-center gap-1 text-xs text-emerald-600 dark:text-emerald-400">
                    <CloudUpload className="h-3 w-3" />
                    Draft saved
                  </span>
                )}
                {autoSaveStatus === "dirty" && (
                  <span className="hidden sm:flex items-center gap-1 text-xs text-amber-500">
                    <Circle className="h-2.5 w-2.5 fill-amber-500" />
                    Unsaved changes
                  </span>
                )}

                <div className="hidden md:inline text-[10px] text-muted-foreground/80 mr-1 self-center">
                  (Auto-save only works in draft status)
                </div>
              </div>
            </div>
          )}
          <Button
            type="button"
            onClick={() => {
              if (templateId) {
                executeSubmit(false, false);
              } else {
                handleSubmit(false);
              }
            }}
            disabled={saving}
            className="px-2 gap-0"
          >
            <Save className="h-4 w-4 mr-2" />
            {saving
              ? "Saving..."
              : templateId
                ? "Save Template"
                : "Save Workout Card"}
          </Button>
        </div>
      </div>

      {/* Stepper Navigation */}
      <div className="w-full max-w-md mx-auto mb-8 bg-card border border-border p-3.5 rounded-2xl shadow-sm">
        <div className="relative flex items-center justify-between">
          {/* Progress bar background line */}
          <div className="absolute left-[15%] right-[15%] top-[18px] h-[2px] bg-secondary -z-0" />
          {/* Progress bar active line */}
          <div
            className="absolute left-[15%] top-[18px] h-[2px] bg-primary transition-all duration-300 -z-0"
            style={{
              width: activeStep === "workouts" ? "70%" : "0%",
            }}
          />

          {/* Step 1: Configuration */}
          <button
            type="button"
            onClick={() => setActiveStep("configuration")}
            className="relative z-10 flex flex-col items-center gap-2 group focus:outline-none"
          >
            <div
              className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold transition-all duration-300 border-2 ${activeStep === "configuration"
                ? "bg-primary border-primary text-primary-foreground shadow-[0_0_15px_rgba(239,68,68,0.3)] ring-4 ring-primary/10"
                : activeStep === "workouts"
                  ? "bg-emerald-500 border-emerald-500 text-white"
                  : "bg-card border-border text-muted-foreground group-hover:border-foreground"
                }`}
            >
              {activeStep === "workouts" ? (
                <CheckCircle2 className="h-5 w-5" />
              ) : (
                "1"
              )}
            </div>
            <span
              className={`text-[10px] md:text-xs font-semibold uppercase tracking-wider transition-colors duration-200 ${activeStep === "configuration"
                ? "text-primary font-bold"
                : activeStep === "workouts"
                  ? "text-emerald-500 font-bold"
                  : "text-muted-foreground group-hover:text-foreground"
                }`}
            >
              Configuration
            </span>
          </button>

          {/* Step 2: Workouts */}
          <button
            type="button"
            onClick={handleNavigateToWorkouts}
            className="relative z-10 flex flex-col items-center gap-2 group focus:outline-none"
          >
            <div
              className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold transition-all duration-300 border-2 ${activeStep === "workouts"
                ? "bg-primary border-primary text-primary-foreground shadow-[0_0_15px_rgba(239,68,68,0.3)] ring-4 ring-primary/10"
                : "bg-card border-border text-muted-foreground group-hover:border-foreground"
                }`}
            >
              2
            </div>
            <span
              className={`text-[10px] md:text-xs font-semibold uppercase tracking-wider transition-colors duration-200 ${activeStep === "workouts"
                ? "text-primary font-bold"
                : "text-muted-foreground group-hover:text-foreground"
                }`}
            >
              Workouts
            </span>
          </button>
        </div>
      </div>

      <div className="w-full">
        {activeStep === "configuration" ? (
          <div className="max-w-3xl mx-auto w-full space-y-6">
            {renderConfiguration()}
            <div className="flex justify-end pt-4 border-t border-border">
              <Button
                type="button"
                onClick={handleNavigateToWorkouts}
                className="bg-primary hover:bg-primary/95 text-primary-foreground font-semibold px-6 py-2.5 flex items-center gap-2 transition-all duration-200 shadow-sm"
              >
                <span>Configure Workouts</span>
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        ) : (
          <div className="max-w-5xl mx-auto w-full space-y-6">
            {/* Apply To Entire Workout Card */}
            <div className="rounded-lg border hidden border-border bg-card shadow-sm overflow-hidden">
              <div
                className="flex items-center justify-between px-2 py-1.5 cursor-pointer select-none hover:bg-secondary/5 transition-all duration-200"
                onClick={() => setShowGlobalCommon((prev) => !prev)}
              >
                <div className="flex items-center gap-2.5">
                  {showGlobalCommon ? (
                    <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" />
                  ) : (
                    <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
                  )}
                  <div className="flex flex-col md:flex-row md:items-center gap-1.5">
                    <h2 className="font-heading text-sm md:text-base font-semibold uppercase tracking-wide text-card-foreground">
                      Apply To Entire Workout Card
                    </h2>
                    <p className="text-[10px] md:text-xs text-muted-foreground">
                      (Applied to all days of all phases)
                    </p>
                  </div>
                </div>
                {(() => {
                  const globalCommonPlan = plans.globalCommon?.common;
                  const globalCommonWorkoutsCount =
                    globalCommonPlan?.sections?.reduce(
                      (sum, s) => sum + (s.workouts?.length || 0),
                      0,
                    ) || 0;
                  return (
                    <div className="text-[10px] sm:text-xs font-semibold text-muted-foreground bg-secondary/30 px-3 py-0.5 rounded-full border border-border/40 select-none">
                      <span className="flex items-center gap-2">
                        {globalCommonWorkoutsCount === 1
                          ? "1 Workout"
                          : `${globalCommonWorkoutsCount} Workouts`}
                      </span>
                    </div>
                  );
                })()}
              </div>
              {showGlobalCommon && (
                <div className="border-t border-border p-1.5 md:p-4 !pt-0">
                  {renderPlanFields(
                    "globalCommon",
                    "common",
                    plans.globalCommon.common,
                  )}
                </div>
              )}
            </div>

            <Tabs
              value={activePhaseTab}
              onValueChange={(val) => {
                setActivePhaseTab(val);
                setActiveWorkoutKey("");
              }}
              className="space-y-0"
            >
              <TabsList className="flex h-auto flex-nowrap overflow-x-auto justify-start border-border border-t border-r border-l border-b bg-card rounded-t-xl rounded-b-none px-4 pt-3 pb-0 gap-6 items-center scrollbar-hide overflow-y-hidden">
                <TabsTrigger
                  value="preparation"
                  className={cn(
                    "font-heading uppercase text-xs font-semibold tracking-wider pb-3 pt-1 px-1 border-b-2 border-transparent rounded-none transition-all duration-200 hover:text-foreground data-[state=active]:text-primary data-[state=active]:border-primary data-[state=active]:shadow-none data-[state=inactive]:bg-transparent data-[state=inactive]:text-muted-foreground -mb-[1px] whitespace-nowrap flex items-center gap-1.5",
                    !enabledTabs.preparation && "opacity-50",
                  )}
                >
                  <span>Fitness Preparation Days</span>
                  {!enabledTabs.preparation && (
                    <span className="text-[9px] lowercase bg-secondary/50 text-muted-foreground px-1.5 py-0.5 rounded-full border border-border/30">
                      off
                    </span>
                  )}
                </TabsTrigger>
                <TabsTrigger
                  value="active"
                  className="font-heading uppercase text-xs font-semibold tracking-wider pb-3 pt-1 px-1 border-b-2 border-transparent rounded-none transition-all duration-200 hover:text-foreground data-[state=active]:text-primary data-[state=active]:border-primary data-[state=active]:shadow-none data-[state=inactive]:bg-transparent data-[state=inactive]:text-muted-foreground -mb-[1px] whitespace-nowrap"
                >
                  Active Days
                </TabsTrigger>
                <TabsTrigger
                  value="rest"
                  className={cn(
                    "font-heading uppercase text-xs font-semibold tracking-wider pb-3 pt-1 px-1 border-b-2 border-transparent rounded-none transition-all duration-200 hover:text-foreground data-[state=active]:text-primary data-[state=active]:border-primary data-[state=active]:shadow-none data-[state=inactive]:bg-transparent data-[state=inactive]:text-muted-foreground -mb-[1px] whitespace-nowrap flex items-center gap-1.5",
                    !enabledTabs.rest && "opacity-50",
                  )}
                >
                  <span>Rest Day</span>
                  {!enabledTabs.rest && (
                    <span className="text-[9px] lowercase bg-secondary/50 text-muted-foreground px-1.5 py-0.5 rounded-full border border-border/30">
                      off
                    </span>
                  )}
                </TabsTrigger>
                <TabsTrigger
                  value="repreparation"
                  className={cn(
                    "font-heading uppercase text-xs font-semibold tracking-wider pb-3 pt-1 px-1 border-b-2 border-transparent rounded-none transition-all duration-200 hover:text-foreground data-[state=active]:text-primary data-[state=active]:border-primary data-[state=active]:shadow-none data-[state=inactive]:bg-transparent data-[state=inactive]:text-muted-foreground -mb-[1px] whitespace-nowrap flex items-center gap-1.5",
                    !enabledTabs.repreparation && "opacity-50",
                  )}
                >
                  <span>Fitness Re-Preparation Days</span>
                  {!enabledTabs.repreparation && (
                    <span className="text-[9px] lowercase bg-secondary/50 text-muted-foreground px-1.5 py-0.5 rounded-full border border-border/30">
                      off
                    </span>
                  )}
                </TabsTrigger>
              </TabsList>
              <TabsContent
                value="preparation"
                className="space-y-3 bg-card border border-border border-t-0 rounded-b-xl p-2 md:p-4 mt-0 rounded-t-none"
              >
                <div className="flex items-center justify-between p-3 bg-secondary/15 rounded-xl border border-border/80 mb-4 text-left">
                  <div className="flex flex-col gap-0.5">
                    <span className="text-xs font-semibold uppercase tracking-wider font-heading">
                      Enable Fitness Preparation Days
                    </span>
                    <span className="text-[10px] text-muted-foreground">
                      Toggle this on to define fitness preparation days before
                      active training starts.
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    {enabledTabs.preparation && (
                      <div className="flex items-center gap-2">
                        <Label className="text-[10px] font-semibold uppercase text-muted-foreground">
                          Prep Days:
                        </Label>
                        <Select
                          value={String(form.fitnessPreparationDays)}
                          onValueChange={(value) =>
                            updateForm({
                              fitnessPreparationDays: Number(value),
                            })
                          }
                        >
                          <SelectTrigger className="h-7 w-20 text-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {Array.from(
                              { length: 10 },
                              (_, index) => index + 1,
                            ).map((day) => (
                              <SelectItem key={day} value={String(day)}>
                                {day} Day{day > 1 ? "s" : ""}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                    <Switch
                      checked={enabledTabs.preparation}
                      onCheckedChange={(checked) =>
                        handleTabToggle("preparation", checked)
                      }
                    />
                  </div>
                </div>
                <div
                  className={cn(
                    "space-y-3",
                    !enabledTabs.preparation &&
                    "opacity-40 pointer-events-none select-none",
                  )}
                >
                  {renderDayTabs(
                    "preparation",
                    "Fitness Preparation Days Workouts",
                    Number(form.fitnessPreparationDays),
                  )}
                </div>
              </TabsContent>
              <TabsContent
                value="repreparation"
                className="space-y-3 bg-card border border-border border-t-0 rounded-b-xl p-2 md:p-4 mt-0 rounded-t-none"
              >
                <div className="flex items-center justify-between p-3 bg-secondary/15 rounded-xl border border-border/80 mb-4 text-left">
                  <div className="flex flex-col gap-0.5">
                    <span className="text-xs font-semibold uppercase tracking-wider font-heading">
                      Enable Fitness Re-Preparation Days
                    </span>
                    <span className="text-[10px] text-muted-foreground">
                      Toggle this on to define fitness re-preparation days after
                      active training completes.
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    {enabledTabs.repreparation && (
                      <div className="flex items-center gap-2">
                        <Label className="text-[10px] font-semibold uppercase text-muted-foreground">
                          Re-Prep Days:
                        </Label>
                        <Select
                          value={String(form.fitnessRePreparationDays)}
                          onValueChange={(value) =>
                            updateForm({
                              fitnessRePreparationDays: Number(value),
                            })
                          }
                        >
                          <SelectTrigger className="h-7 w-20 text-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {Array.from(
                              { length: 10 },
                              (_, index) => index + 1,
                            ).map((day) => (
                              <SelectItem key={day} value={String(day)}>
                                {day} Day{day > 1 ? "s" : ""}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                    <Switch
                      checked={enabledTabs.repreparation}
                      onCheckedChange={(checked) =>
                        handleTabToggle("repreparation", checked)
                      }
                    />
                  </div>
                </div>
                <div
                  className={cn(
                    "space-y-3",
                    !enabledTabs.repreparation &&
                    "opacity-40 pointer-events-none select-none",
                  )}
                >
                  {renderDayTabs(
                    "repreparation",
                    "Fitness Re-Preparation Days Workouts",
                    Number(form.fitnessRePreparationDays),
                  )}
                </div>
              </TabsContent>
              <TabsContent
                value="active"
                className="space-y-3 bg-card border border-border border-t-0 rounded-b-xl p-2 md:p-4 mt-0 rounded-t-none"
              >
                {renderDayTabs(
                  "active",
                  "Active Days Workouts",
                  activeDayCount,
                )}
              </TabsContent>
              <TabsContent
                value="rest"
                className="space-y-3 bg-card border border-border border-t-0 rounded-b-xl p-2 md:p-4 mt-0 rounded-t-none"
              >
                <div className="flex items-center justify-between p-3 bg-secondary/15 rounded-xl border border-border/80 mb-4 text-left">
                  <div className="flex flex-col gap-0.5">
                    <span className="text-xs font-semibold uppercase tracking-wider font-heading">
                      Enable Rest Days
                    </span>
                    <span className="text-[10px] text-muted-foreground">
                      Toggle this on to configure workout plans for rest days.
                    </span>
                  </div>
                  <Switch
                    checked={enabledTabs.rest}
                    onCheckedChange={(checked) =>
                      handleTabToggle("rest", checked)
                    }
                  />
                </div>
                <div
                  className={cn(
                    "space-y-3",
                    !enabledTabs.rest &&
                    "opacity-40 pointer-events-none select-none",
                  )}
                >
                  {renderDayTabs("rest", "Rest Day Workouts", restDayCount)}
                </div>
              </TabsContent>
            </Tabs>
            <div className="flex justify-start pt-6 border-t border-border">
              <Button
                type="button"
                variant="outline"
                onClick={() => setActiveStep("configuration")}
                className="flex items-center gap-2"
              >
                <ArrowLeft className="h-4 w-4" />
                <span>Back to Configuration</span>
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Section Delete Confirmation Dialog */}
      <AlertDialog
        open={deleteSectionConfirm !== null}
        onOpenChange={(open) => {
          if (!open) setDeleteSectionConfirm(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading uppercase tracking-wide">
              Delete section?
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="text-muted-foreground text-sm space-y-2 mt-2">
                <p>This will remove:</p>
                <ul className="space-y-1 text-foreground font-medium pl-1">
                  <li>
                    • {deleteSectionConfirm?.workoutCount}{" "}
                    {deleteSectionConfirm?.workoutCount === 1
                      ? "workout"
                      : "workouts"}
                  </li>
                  <li>• all workout data</li>
                </ul>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive hover:bg-destructive/90 text-destructive-foreground"
              onClick={() => {
                if (deleteSectionConfirm) {
                  removeSection(
                    deleteSectionConfirm.phase,
                    deleteSectionConfirm.dayKey,
                    deleteSectionConfirm.sectionId,
                  );
                  setDeleteSectionConfirm(null);
                }
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      {/* Workout Delete Confirmation Dialog */}
      <AlertDialog
        open={deleteWorkoutConfirm !== null}
        onOpenChange={(open) => {
          if (!open) setDeleteWorkoutConfirm(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading uppercase tracking-wide">
              Delete workout?
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="text-muted-foreground text-sm space-y-2 mt-2">
                <p>This will remove:</p>
                <ul className="space-y-1 text-foreground font-medium pl-1">
                  <li>• {deleteWorkoutConfirm?.workoutTitle}</li>
                  <li>• all sets and exercise details</li>
                </ul>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive hover:bg-destructive/90 text-destructive-foreground"
              onClick={() => {
                if (deleteWorkoutConfirm) {
                  removeWorkout(
                    deleteWorkoutConfirm.phase,
                    deleteWorkoutConfirm.dayKey,
                    deleteWorkoutConfirm.section,
                    deleteWorkoutConfirm.workoutIndex,
                  );
                  setDeleteWorkoutConfirm(null);
                }
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Copy Plan Confirmation Dialog */}
      <AlertDialog
        open={copyPlanConfirm !== null}
        onOpenChange={(open) => {
          if (!open) setCopyPlanConfirm(null);
        }}
      >
        <AlertDialogContent className="bg-card border-border max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading uppercase tracking-wide">
              Copy Workout Plan
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="text-muted-foreground text-sm space-y-2 mt-2">
                <p>
                  The target day already has workouts configured. How would you
                  like to apply the plan from{" "}
                  <strong className="text-foreground">
                    {copyPlanConfirm?.sourceLabel}
                  </strong>
                  ?
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex flex-col sm:flex-row gap-2">
            <AlertDialogCancel onClick={() => setCopyPlanConfirm(null)}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              className="bg-primary hover:bg-primary/95 text-primary-foreground"
              onClick={() => {
                if (copyPlanConfirm) {
                  copyDayPlan(
                    copyPlanConfirm.targetPhase,
                    copyPlanConfirm.targetDayKey,
                    copyPlanConfirm.sourcePhase,
                    copyPlanConfirm.sourceDayKey,
                    copyPlanConfirm.sourceLabel,
                    "append",
                  );
                  setCopyPlanConfirm(null);
                }
              }}
            >
              Add to List (Append)
            </AlertDialogAction>
            <AlertDialogAction
              className="bg-destructive hover:bg-destructive/95 text-white"
              onClick={() => {
                if (copyPlanConfirm) {
                  copyDayPlan(
                    copyPlanConfirm.targetPhase,
                    copyPlanConfirm.targetDayKey,
                    copyPlanConfirm.sourcePhase,
                    copyPlanConfirm.sourceDayKey,
                    copyPlanConfirm.sourceLabel,
                    "replace",
                  );
                  setCopyPlanConfirm(null);
                }
              }}
            >
              Replace Existing
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      {/* Rest Day Change Confirmation Dialog */}
      <AlertDialog
        open={restDayConfirm !== null}
        onOpenChange={(open) => {
          if (!open) setRestDayConfirm(null);
        }}
      >
        <AlertDialogContent className="bg-card border-border max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading uppercase tracking-wide text-destructive">
              Warning: Workouts will be deleted
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="text-muted-foreground text-sm space-y-2 mt-2">
                <p>
                  Adding another rest day will reduce the number of active
                  workout days.
                </p>
                <p className="font-semibold text-foreground">
                  The following day(s) have workouts that will be permanently
                  deleted:
                </p>
                <ul className="list-disc list-inside pl-2 text-foreground font-medium">
                  {restDayConfirm?.daysWithWorkouts?.map((dayNum) => (
                    <li key={dayNum}>Day {dayNum}</li>
                  ))}
                </ul>
                <p className="text-xs text-destructive mt-2">
                  This action cannot be undone. Are you sure you want to
                  proceed?
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setRestDayConfirm(null)}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive hover:bg-destructive/90 text-white"
              onClick={() => {
                if (restDayConfirm) {
                  updateForm({ restDays: restDayConfirm.restDays });
                  setRestDayConfirm(null);
                }
              }}
            >
              Proceed
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Reassignment Completed/Overdue Workouts Override Dialog */}
      <AlertDialog
        open={showOverrideConfirm}
        onOpenChange={setShowOverrideConfirm}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading uppercase tracking-wide">
              Keep or Override Completed Workouts?
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="text-muted-foreground text-sm space-y-2 mt-2">
                <p>
                  This member has an existing workout schedule. Would you like
                  to override already completed and overdue workouts with the
                  new plan?
                </p>
                <p className="text-xs text-amber-500 font-medium">
                  • <strong>No (Affect upcoming only):</strong> Keep completed
                  and overdue workouts exactly as they are. Only update future
                  upcoming workouts.
                </p>
                <p className="text-xs text-emerald-500 font-medium">
                  • <strong>Yes (Override all):</strong> Replace all workouts
                  across the entire schedule, including completed ones.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col sm:flex-row gap-2">
            <AlertDialogCancel onClick={() => setShowOverrideConfirm(false)}>
              Cancel
            </AlertDialogCancel>
            <Button
              variant="outline"
              className="border-amber-500/30 text-amber-500 hover:bg-amber-500/10 hover:text-amber-500 font-heading uppercase tracking-wide text-xs"
              onClick={() => executeSubmit(false, false)}
              disabled={saving}
            >
              No, Keep Completed
            </Button>
            <AlertDialogAction
              className="bg-emerald-600 hover:bg-emerald-700 font-heading uppercase tracking-wide text-xs"
              onClick={() => executeSubmit(false, true)}
              disabled={saving}
            >
              Yes, Override All
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Resume Draft Confirmation Dialog */}
      <AlertDialog
        open={showResumeDraftDialog}
        onOpenChange={setShowResumeDraftDialog}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading uppercase tracking-wide">
              Resume editing draft?
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="text-muted-foreground text-sm space-y-2 mt-2">
                <p>
                  An unsaved draft plan was found for this member. Would you
                  like to resume editing this draft or start fresh?
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={() => {
                setDraftAssignment(null);
                setShowResumeDraftDialog(false);
              }}
            >
              Start Fresh
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleLoadDraft}>
              Load Draft
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Load Template Confirmation Dialog */}
      <AlertDialog
        open={showTemplateConfirmDialog}
        onOpenChange={setShowTemplateConfirmDialog}
      >
        <AlertDialogContent className="bg-card border-border max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading uppercase tracking-wide">
              Load Template
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="text-muted-foreground text-sm space-y-2 mt-2">
                <p>
                  Are you sure you want to load template{" "}
                  <strong className="text-foreground">
                    {selectedTemplateToLoad?.name ||
                      selectedTemplateToLoad?.cardName}
                  </strong>
                  ?
                </p>
                <p className="text-destructive font-medium">
                  ⚠️ This will completely override all existing workouts, days,
                  and configuration on this card. This action cannot be undone.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={() => {
                setShowTemplateConfirmDialog(false);
                setSelectedTemplateToLoad(null);
              }}
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive hover:bg-destructive/90 text-white font-heading uppercase tracking-wide text-xs"
              onClick={handleConfirmLoadTemplate}
            >
              Override & Load
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Save Workout Card & Template Dialog */}
      <Dialog open={showSaveConfirm} onOpenChange={setShowSaveConfirm}>
        <DialogContent className="max-w-md bg-card border-border">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase tracking-wide">
              Save Workout Card
            </DialogTitle>
            <DialogDescription>
              Confirm details and save the workout card assignment.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="flex items-center justify-between gap-3 p-3 rounded-lg border border-border bg-secondary/20">
              <div>
                <p className="text-sm font-medium">Save this as template</p>
                <p className="text-xs text-muted-foreground">
                  Available later for other members.
                </p>
              </div>
              <Switch
                checked={form.saveAsTemplate}
                onCheckedChange={(saveAsTemplate) =>
                  updateForm({ saveAsTemplate })
                }
              />
            </div>
            {form.saveAsTemplate && (
              <div className="space-y-2">
                <Label className="text-xs font-heading uppercase">
                  Template Name <span className="text-destructive">*</span>
                </Label>
                <Input
                  value={form.templateName}
                  onChange={(event) =>
                    updateForm({ templateName: event.target.value })
                  }
                  placeholder="Enter template name"
                  className={cn(
                    form.saveAsTemplate &&
                    !form.templateName.trim() &&
                    "border-destructive",
                  )}
                />
                {form.saveAsTemplate && !form.templateName.trim() && (
                  <p className="text-xs text-destructive">
                    Template name is required
                  </p>
                )}
              </div>
            )}
          </div>
          <DialogFooter className="gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowSaveConfirm(false)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={async () => {
                if (form.saveAsTemplate && !form.templateName.trim()) {
                  toast.error("Template name is required");
                  return;
                }
                setShowSaveConfirm(false);
                if (reassignId) {
                  setShowOverrideConfirm(true);
                } else {
                  await executeSubmit(false, false);
                }
              }}
              disabled={saving}
            >
              {saving ? "Saving..." : "Confirm & Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Section Dialog */}
      <Dialog
        open={addSectionDialog !== null}
        onOpenChange={(open) => {
          if (!open) setAddSectionDialog(null);
        }}
      >
        <DialogContent className="max-w-md bg-card border-border">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase tracking-wide">
              Add Workout Section
            </DialogTitle>
            <DialogDescription>
              Provide a name for the new workout section (e.g., Warmup, Core,
              Conditioning, Main Lift).
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label
                htmlFor="new-section-name"
                className="text-xs font-heading uppercase"
              >
                Section Name
              </Label>
              <Input
                id="new-section-name"
                value={newSectionName}
                onChange={(e) => setNewSectionName(e.target.value)}
                placeholder="e.g. Warmup, Cooldown, Main Lift..."
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    handleConfirmAddSection();
                  }
                }}
              />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setAddSectionDialog(null)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleConfirmAddSection}
              disabled={!newSectionName.trim()}
            >
              Add Section
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
